import React, { useState, useEffect } from 'react';
import { 
  Target, DollarSign, Calendar, Globe, Zap, CheckCircle, 
  AlertCircle, ChevronDown, ChevronUp, Info, Rocket, ArrowRight,
  Plus, Trash2, Edit2, Save, X, Sparkles, RefreshCw, CheckSquare, Square, Copy
} from 'lucide-react';
import { formatNumber, formatCurrency } from '../utils/formatting';
import { useAuth } from '@clerk/clerk-react';
import { apiCallJson } from '../utils/api';

const API_URL = `${process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001'}/api`;
const MIN_MULTI_AD_GROUPS = 2;
const MAX_MULTI_AD_GROUPS = 6;

const createEmptyAdGroup = (index = 0, overrides = {}) => ({
  id: overrides.id || `adgroup-${Date.now()}-${index}`,
  name: overrides.name || '',
  keywords: overrides.keywords || [],
  keywordInput: '',
  matchType: 'BROAD',
  maxCpc: overrides.maxCpc || 1.0,
  finalUrl: overrides.finalUrl || 'https://',
  path1: overrides.path1 || '',
  path2: overrides.path2 || '',
  headlines: overrides.headlines || ['', '', ''],
  descriptions: overrides.descriptions || ['', '']
});

export default function CampaignBuilder({ initialKeywords = [], initialLanguage = 'nl', initialData = null, onClose }) {
  const { getToken } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [creating, setCreating] = useState(false);
  const [createResult, setCreateResult] = useState(null);
  
  // Publishing state
  const [showPublishModal, setShowPublishModal] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [publishResult, setPublishResult] = useState(null);
  const [draftCampaignId, setDraftCampaignId] = useState(null);
  
  // AI Generation state
  const [showAiModal, setShowAiModal] = useState(false);
  const [aiGenerating, setAiGenerating] = useState(false);
  const [businessInfo, setBusinessInfo] = useState({ website: '', usps: '' });
  const [landingPageUrl, setLandingPageUrl] = useState('');
  const [analyzeCompetitors, setAnalyzeCompetitors] = useState(false);
  const [descriptionTypes, setDescriptionTypes] = useState(['informative', 'urgency', 'commercial', 'strong-cta']);
  
  // Campaign Type
  const [campaignType, setCampaignType] = useState('SEARCH');
  
  // Campaign Basic Info
  const [campaignName, setCampaignName] = useState('');
  const [adGroupName, setAdGroupName] = useState('');
  const [adGroupMode, setAdGroupMode] = useState('single'); // single or multiple
  const [multiAdGroups, setMultiAdGroups] = useState(() => [
    createEmptyAdGroup(0),
    createEmptyAdGroup(1)
  ]);
  
  // Budget Settings
  const [budgetType, setBudgetType] = useState('daily'); // daily or total
  const [budgetAmount, setBudgetAmount] = useState(10);
  const [deliveryMethod, setDeliveryMethod] = useState('STANDARD'); // STANDARD or ACCELERATED
  
  // Bidding Strategy
  const [biddingStrategy, setBiddingStrategy] = useState('MANUAL_CPC');
  const [targetCpa, setTargetCpa] = useState(10);
  const [targetRoas, setTargetRoas] = useState(400);
  
  // Network Settings
  const [networkSettings, setNetworkSettings] = useState({
    targetGoogleSearch: true,
    targetSearchNetwork: true,
    targetContentNetwork: false, // Display Expansion
    targetPartnerSearchNetwork: false
  });
  
  // Campaign Settings
  const [campaignStatus, setCampaignStatus] = useState('PAUSED');
  const [language, setLanguage] = useState(initialLanguage);
  const [selectedCountries, setSelectedCountries] = useState([]);
  const [showCountryModal, setShowCountryModal] = useState(false);
  const [tempSelectedCountries, setTempSelectedCountries] = useState([]);
  const [countryModalShownOnce, setCountryModalShownOnce] = useState(false);
  const [extraCountrySearch, setExtraCountrySearch] = useState('');
  const [showExtraCountryDropdown, setShowExtraCountryDropdown] = useState(false);
  const [cityInput, setCityInput] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  
  // Path fields
  const [path1, setPath1] = useState('');
  const [path2, setPath2] = useState('');
  
  // Extensions
  const [sitelinks, setSitelinks] = useState([]);
  const [callouts, setCallouts] = useState([]);
  const [structuredSnippets, setStructuredSnippets] = useState([]);
  const [extensionsMode, setExtensionsMode] = useState('none'); // 'none', 'ai', 'manual'
  
  // Performance Max / Demand Gen / Display Assets
  const [assetGroupName, setAssetGroupName] = useState('Asset Group 1');
  const [imageUrls, setImageUrls] = useState({ landscape: [], square: [] });
  const [businessName, setBusinessName] = useState('');
  const [callToAction, setCallToAction] = useState('LEARN_MORE');
  const [longHeadline, setLongHeadline] = useState('');
  
  // Load initial keywords if provided (from Keyword Research)
  useEffect(() => {
    if (initialKeywords && initialKeywords.length > 0 && !initialData) {
      const formattedKeywords = initialKeywords.map(kw => ({
        text: kw.keyword,
        matchType: kw.matchType || 'BROAD',
        maxCpc: parseFloat(kw.cpc) || 1.0
      }));
      setKeywords(formattedKeywords);
      
      if (initialKeywords[0]) {
        setCampaignName(`${initialKeywords[0].keyword} Campaign`);
        setAdGroupName(`${initialKeywords[0].keyword} Ad Group`);
        setMultiAdGroups(prev => prev.map((group, index) => index === 0
          ? {
              ...group,
              name: `${initialKeywords[0].keyword} Ad Group`,
              keywords: formattedKeywords
            }
          : group
        ));
      }
      
      setCurrentStep(1);
    }
  }, [initialKeywords, initialData]);
  
  // Load AI-generated campaign data if provided
  useEffect(() => {
    if (initialData) {
      console.log('Loading AI-generated campaign data:', initialData);
      
      // Campaign Info
      setCampaignName(initialData.name || '');
      setAdGroupName(`${initialData.name} Ad Group` || 'Ad Group 1');
      
      // Budget
      setBudgetAmount(initialData.budget || 10);
      
      // Languages & Locations
      if (initialData.languages && initialData.languages.length > 0) {
        setLanguage(initialData.languages[0]); // Set first language as primary
      }
      if (initialData.locations && initialData.locations.length > 0) {
        // Convert location strings to location objects
        console.log('🎯 Incoming locations from AI:', initialData.locations);
        const locationObjects = initialData.locations.map(loc => getLocationObject(loc));
        console.log('🗺️ Converted to objects:', locationObjects);
        setSelectedCountries(locationObjects);
      }
      
      // Bidding Strategy
      if (initialData.biddingStrategy) {
        setBiddingStrategy(initialData.biddingStrategy);
      }
      
      // Network Settings
      if (initialData.networkSettings) {
        setNetworkSettings(initialData.networkSettings);
      }
      
      // Keywords
      if (initialData.keywords && initialData.keywords.length > 0) {
        const formattedKeywords = initialData.keywords.map(kw => ({
          text: kw.keyword,
          matchType: kw.matchType || 'BROAD',
          maxCpc: parseFloat(kw.highTopOfPageBid) || parseFloat(kw.lowTopOfPageBid) || 1.0
        }));
        setKeywords(formattedKeywords);
        setMultiAdGroups(prev => prev.map((group, index) => index === 0
          ? {
              ...group,
              name: `${initialData.name || 'Campaign'} Ad Group`,
              keywords: formattedKeywords
            }
          : group
        ));
      }
      
      // Start at step 1 so user can verify/edit campaign name
      setCurrentStep(1);
    }
  }, [initialData]);
  
  // Watch for language changes and auto-add countries
  useEffect(() => {
    if (language === 'nl') {
      // Auto-select Netherlands and Belgium for Dutch
      setSelectedCountries(prev => {
        const newCountries = [...prev];
        const hasNetherlands = newCountries.some(c => 
          (typeof c === 'string' && (c === 'Netherlands' || c === 'Nederland')) ||
          (typeof c === 'object' && (c.name === 'Netherlands' || c.code === 'NL'))
        );
        const hasBelgium = newCountries.some(c => 
          (typeof c === 'string' && (c === 'Belgium' || c === 'België')) ||
          (typeof c === 'object' && (c.name === 'Belgium' || c.code === 'BE'))
        );
        
        if (!hasNetherlands) {
          newCountries.push(getLocationObject('Netherlands'));
        }
        if (!hasBelgium) {
          newCountries.push(getLocationObject('Belgium'));
        }
        return newCountries;
      });
    }
  }, [language]);
  
  // Ad Group & Keywords
  const [keywords, setKeywords] = useState([]);
  const [keywordInput, setKeywordInput] = useState('');
  const [matchType, setMatchType] = useState('BROAD');
  const [maxCpcBid, setMaxCpcBid] = useState(1.0);

  // Campaign Advisor (data-driven advice on type, bidding, budget)
  const [advisorGoal, setAdvisorGoal] = useState('leads');
  const [advice, setAdvice] = useState(null);
  const [adviceLoading, setAdviceLoading] = useState(false);
  const [adviceError, setAdviceError] = useState(null);
  
  // Ads
  const [headlines, setHeadlines] = useState(['', '', '']);
  const [descriptions, setDescriptions] = useState(['', '']);
  const [finalUrl, setFinalUrl] = useState('https://');
  const isMultipleAdGroupMode = campaignType === 'SEARCH' && adGroupMode === 'multiple';
  
  // Advanced
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [containsEuPolitical, setContainsEuPolitical] = useState(false);

  const availableLanguages = [
    { code: 'nl', name: 'Nederlands', country: 'NL', flag: '🇳🇱' },
    { code: 'en', name: 'English', country: 'US', flag: '🇺🇸' },
    { code: 'es', name: 'Español', country: 'ES', flag: '🇪🇸' },
    { code: 'de', name: 'Deutsch', country: 'DE', flag: '🇩🇪' },
    { code: 'fr', name: 'Français', country: 'FR', flag: '🇫🇷' },
    { code: 'it', name: 'Italiano', country: 'IT', flag: '🇮🇹' },
    { code: 'pt', name: 'Português', country: 'PT', flag: '🇵🇹' },
    { code: 'sv', name: 'Svenska', country: 'SE', flag: '🇸🇪' },
    { code: 'no', name: 'Norsk', country: 'NO', flag: '🇳🇴' },
    { code: 'fi', name: 'Suomi', country: 'FI', flag: '🇫🇮' },
    { code: 'pl', name: 'Polski', country: 'PL', flag: '🇵🇱' },
    { code: 'cs', name: 'Čeština', country: 'CZ', flag: '🇨🇿' },
    { code: 'da', name: 'Dansk', country: 'DK', flag: '🇩🇰' },
    { code: 'ro', name: 'Română', country: 'RO', flag: '🇷🇴' },
    { code: 'el', name: 'Ελληνικά', country: 'GR', flag: '🇬🇷' },
    { code: 'tr', name: 'Türkçe', country: 'TR', flag: '🇹🇷' },
    { code: 'hu', name: 'Magyar', country: 'HU', flag: '🇭🇺' }
  ];

  // Helper: Convert location string to location object
  const getLocationObject = (locationString) => {
    const locationMapping = {
      // Countries
      'Netherlands': { code: 'NL', name: 'Netherlands', geoCode: '2528', flag: '🇳🇱', type: 'country' },
      'Belgium': { code: 'BE', name: 'Belgium', geoCode: '2056', flag: '🇧🇪', type: 'country' },
      'Spain': { code: 'ES', name: 'Spain', geoCode: '2724', flag: '🇪🇸', type: 'country' },
      'France': { code: 'FR', name: 'France', geoCode: '2250', flag: '🇫🇷', type: 'country' },
      'Germany': { code: 'DE', name: 'Germany', geoCode: '2276', flag: '🇩🇪', type: 'country' },
      'Italy': { code: 'IT', name: 'Italy', geoCode: '2380', flag: '🇮🇹', type: 'country' },
      'Portugal': { code: 'PT', name: 'Portugal', geoCode: '2620', flag: '🇵🇹', type: 'country' },
      'United Kingdom': { code: 'GB', name: 'United Kingdom', geoCode: '2826', flag: '🇬🇧', type: 'country' },
      'Indonesia': { code: 'ID', name: 'Indonesia', geoCode: '2360', flag: '🇮🇩', type: 'country' },
      'United States': { code: 'US', name: 'United States', geoCode: '2840', flag: '🇺🇸', type: 'country' },
      // Cities
      'Amsterdam': { code: 'AMS', name: 'Amsterdam', geoCode: '1010543', flag: '🏙️', type: 'city' },
      'Valencia': { code: 'VLC', name: 'Valencia', geoCode: '1007309', flag: '🏙️', type: 'city' },
      'Barcelona': { code: 'BCN', name: 'Barcelona', geoCode: '1004531', flag: '🏙️', type: 'city' },
      'Madrid': { code: 'MAD', name: 'Madrid', geoCode: '1005583', flag: '🏙️', type: 'city' },
      'Paris': { code: 'PAR', name: 'Paris', geoCode: '1006094', flag: '🏙️', type: 'city' },
      'London': { code: 'LON', name: 'London', geoCode: '1006886', flag: '🏙️', type: 'city' },
      'Berlin': { code: 'BER', name: 'Berlin', geoCode: '1004234', flag: '🏙️', type: 'city' },
      'Rome': { code: 'ROM', name: 'Rome', geoCode: '1007390', flag: '🏙️', type: 'city' },
      'Lisbon': { code: 'LIS', name: 'Lisbon', geoCode: '1011747', flag: '🏙️', type: 'city' },
      'Bali': { code: 'BAL', name: 'Bali', geoCode: '1000002', flag: '🏙️', type: 'city' },
      'Prague': { code: 'PRG', name: 'Prague', geoCode: '1009114', flag: '🏙️', type: 'city' },
      'Vienna': { code: 'VIE', name: 'Vienna', geoCode: '1009933', flag: '🏙️', type: 'city' },
      'Copenhagen': { code: 'CPH', name: 'Copenhagen', geoCode: '1004648', flag: '🏙️', type: 'city' },
      'Stockholm': { code: 'STO', name: 'Stockholm', geoCode: '1007482', flag: '🏙️', type: 'city' },
      'Dublin': { code: 'DUB', name: 'Dublin', geoCode: '1007447', flag: '🏙️', type: 'city' },
      'Warsaw': { code: 'WAW', name: 'Warsaw', geoCode: '1011521', flag: '🏙️', type: 'city' },
      'Athens': { code: 'ATH', name: 'Athens', geoCode: '1004641', flag: '🏙️', type: 'city' },
      'Budapest': { code: 'BUD', name: 'Budapest', geoCode: '1006097', flag: '🏙️', type: 'city' },
      'Brussels': { code: 'BRU', name: 'Brussels', geoCode: '1000672', flag: '🏙️', type: 'city' },
      'Milan': { code: 'MIL', name: 'Milan', geoCode: '1006885', flag: '🏙️', type: 'city' }
    };
    
    return locationMapping[locationString] || { 
      code: locationString.substring(0, 3).toUpperCase(), 
      name: locationString, 
      geoCode: '2724', 
      flag: '🌍', 
      type: 'custom' 
    };
  };

  const languageCountries = {
    nl: [
      { code: 'NL', name: 'Netherlands', geoCode: '2528', flag: '🇳🇱' },
      { code: 'BE', name: 'Belgium (Flanders)', geoCode: '2056', flag: '🇧🇪' },
      { code: 'SR', name: 'Suriname', geoCode: '2740', flag: '🇸🇷' }
    ],
    en: [
      { code: 'US', name: 'United States', geoCode: '2840', flag: '🇺🇸' },
      { code: 'GB', name: 'United Kingdom', geoCode: '2826', flag: '🇬🇧' },
      { code: 'CA', name: 'Canada', geoCode: '2124', flag: '🇨🇦' },
      { code: 'AU', name: 'Australia', geoCode: '2036', flag: '🇦🇺' },
      { code: 'NZ', name: 'New Zealand', geoCode: '2554', flag: '🇳🇿' },
      { code: 'IE', name: 'Ireland', geoCode: '2372', flag: '🇮🇪' },
      { code: 'ZA', name: 'South Africa', geoCode: '2710', flag: '🇿🇦' },
      { code: 'SG', name: 'Singapore', geoCode: '2702', flag: '🇸🇬' },
      { code: 'IN', name: 'India', geoCode: '2356', flag: '🇮🇳' }
    ],
    de: [
      { code: 'DE', name: 'Germany', geoCode: '2276', flag: '🇩🇪' },
      { code: 'AT', name: 'Austria', geoCode: '2040', flag: '🇦🇹' },
      { code: 'CH', name: 'Switzerland', geoCode: '2756', flag: '🇨🇭' },
      { code: 'LI', name: 'Liechtenstein', geoCode: '2438', flag: '🇱🇮' },
      { code: 'LU', name: 'Luxembourg', geoCode: '2442', flag: '🇱🇺' },
      { code: 'BE', name: 'Belgium', geoCode: '2056', flag: '🇧🇪' }
    ],
    es: [
      { code: 'ES', name: 'Spain', geoCode: '2724', flag: '🇪🇸' },
      { code: 'MX', name: 'Mexico', geoCode: '2484', flag: '🇲🇽' },
      { code: 'AR', name: 'Argentina', geoCode: '2032', flag: '🇦🇷' },
      { code: 'CO', name: 'Colombia', geoCode: '2170', flag: '🇨🇴' },
      { code: 'CL', name: 'Chile', geoCode: '2152', flag: '🇨🇱' },
      { code: 'PE', name: 'Peru', geoCode: '2604', flag: '🇵🇪' },
      { code: 'VE', name: 'Venezuela', geoCode: '2862', flag: '🇻🇪' },
      { code: 'EC', name: 'Ecuador', geoCode: '2218', flag: '🇪🇨' },
      { code: 'GT', name: 'Guatemala', geoCode: '2320', flag: '🇬🇹' },
      { code: 'CU', name: 'Cuba', geoCode: '2192', flag: '🇨🇺' },
      { code: 'BO', name: 'Bolivia', geoCode: '2068', flag: '🇧🇴' },
      { code: 'DO', name: 'Dominican Republic', geoCode: '2214', flag: '🇩🇴' },
      { code: 'HN', name: 'Honduras', geoCode: '2340', flag: '🇭🇳' },
      { code: 'PY', name: 'Paraguay', geoCode: '2600', flag: '🇵🇾' },
      { code: 'SV', name: 'El Salvador', geoCode: '2222', flag: '🇸🇻' },
      { code: 'NI', name: 'Nicaragua', geoCode: '2558', flag: '🇳🇮' },
      { code: 'CR', name: 'Costa Rica', geoCode: '2188', flag: '🇨🇷' },
      { code: 'PA', name: 'Panama', geoCode: '2591', flag: '🇵🇦' },
      { code: 'UY', name: 'Uruguay', geoCode: '2858', flag: '🇺🇾' },
      { code: 'PR', name: 'Puerto Rico', geoCode: '2630', flag: '🇵🇷' }
    ],
    it: [
      { code: 'IT', name: 'Italy', geoCode: '2380', flag: '🇮🇹' },
      { code: 'CH', name: 'Switzerland', geoCode: '2756', flag: '🇨🇭' },
      { code: 'SM', name: 'San Marino', geoCode: '2674', flag: '🇸🇲' },
      { code: 'VA', name: 'Vatican City', geoCode: '2336', flag: '🇻🇦' }
    ],
    fr: [
      { code: 'FR', name: 'France', geoCode: '2250', flag: '🇫🇷' },
      { code: 'BE', name: 'Belgium (Wallonia)', geoCode: '2056', flag: '🇧🇪' },
      { code: 'CH', name: 'Switzerland', geoCode: '2756', flag: '🇨🇭' },
      { code: 'CA', name: 'Canada (Quebec)', geoCode: '2124', flag: '🇨🇦' },
      { code: 'LU', name: 'Luxembourg', geoCode: '2442', flag: '🇱🇺' },
      { code: 'MC', name: 'Monaco', geoCode: '2492', flag: '🇲🇨' },
      { code: 'SN', name: 'Senegal', geoCode: '2686', flag: '🇸🇳' },
      { code: 'CI', name: 'Côte d\'Ivoire', geoCode: '2384', flag: '🇨🇮' },
      { code: 'ML', name: 'Mali', geoCode: '2466', flag: '🇲🇱' },
      { code: 'BF', name: 'Burkina Faso', geoCode: '2854', flag: '🇧🇫' },
      { code: 'NE', name: 'Niger', geoCode: '2562', flag: '🇳🇪' },
      { code: 'TD', name: 'Chad', geoCode: '2148', flag: '🇹🇩' },
      { code: 'BJ', name: 'Benin', geoCode: '2204', flag: '🇧🇯' },
      { code: 'TG', name: 'Togo', geoCode: '2768', flag: '🇹🇬' },
      { code: 'CF', name: 'Central African Republic', geoCode: '2140', flag: '🇨🇫' },
      { code: 'CG', name: 'Congo', geoCode: '2178', flag: '🇨🇬' },
      { code: 'GA', name: 'Gabon', geoCode: '2266', flag: '🇬🇦' },
      { code: 'GQ', name: 'Equatorial Guinea', geoCode: '2226', flag: '🇬🇶' },
      { code: 'DJ', name: 'Djibouti', geoCode: '2262', flag: '🇩🇯' },
      { code: 'KM', name: 'Comoros', geoCode: '2174', flag: '🇰🇲' },
      { code: 'MG', name: 'Madagascar', geoCode: '2450', flag: '🇲🇬' },
      { code: 'HT', name: 'Haiti', geoCode: '2332', flag: '🇭🇹' }
    ],
    pt: [
      { code: 'PT', name: 'Portugal', geoCode: '2620', flag: '🇵🇹' },
      { code: 'BR', name: 'Brazil', geoCode: '2076', flag: '🇧🇷' },
      { code: 'AO', name: 'Angola', geoCode: '2024', flag: '🇦🇴' },
      { code: 'MZ', name: 'Mozambique', geoCode: '2508', flag: '🇲🇿' },
      { code: 'GW', name: 'Guinea-Bissau', geoCode: '2624', flag: '🇬🇼' },
      { code: 'TL', name: 'East Timor', geoCode: '2626', flag: '🇹🇱' },
      { code: 'MO', name: 'Macau', geoCode: '2446', flag: '🇲🇴' },
      { code: 'CV', name: 'Cape Verde', geoCode: '2132', flag: '🇨🇻' },
      { code: 'ST', name: 'São Tomé and Príncipe', geoCode: '2678', flag: '🇸🇹' }
    ],
    sv: [
      { code: 'SE', name: 'Sweden', geoCode: '2752', flag: '🇸🇪' },
      { code: 'FI', name: 'Finland (Swedish)', geoCode: '2246', flag: '🇫🇮' },
      { code: 'AX', name: 'Åland Islands', geoCode: '2248', flag: '🇦🇽' }
    ],
    no: [
      { code: 'NO', name: 'Norway', geoCode: '2578', flag: '🇳🇴' },
      { code: 'SJ', name: 'Svalbard and Jan Mayen', geoCode: '2744', flag: '🇸🇯' }
    ],
    fi: [
      { code: 'FI', name: 'Finland', geoCode: '2246', flag: '🇫🇮' }
    ],
    pl: [
      { code: 'PL', name: 'Poland', geoCode: '2616', flag: '🇵🇱' }
    ],
    cs: [
      { code: 'CZ', name: 'Czech Republic', geoCode: '2203', flag: '🇨🇿' },
      { code: 'SK', name: 'Slovakia', geoCode: '2703', flag: '🇸🇰' }
    ],
    da: [
      { code: 'DK', name: 'Denmark', geoCode: '2208', flag: '🇩🇰' },
      { code: 'GL', name: 'Greenland', geoCode: '2304', flag: '🇬🇱' },
      { code: 'FO', name: 'Faroe Islands', geoCode: '2234', flag: '🇫🇴' }
    ],
    ro: [
      { code: 'RO', name: 'Romania', geoCode: '2642', flag: '🇷🇴' },
      { code: 'MD', name: 'Moldova', geoCode: '2498', flag: '🇲🇩' }
    ],
    el: [
      { code: 'GR', name: 'Greece', geoCode: '2300', flag: '🇬🇷' },
      { code: 'CY', name: 'Cyprus', geoCode: '2196', flag: '🇨🇾' }
    ],
    tr: [
      { code: 'TR', name: 'Turkey', geoCode: '2792', flag: '🇹🇷' },
      { code: 'CY', name: 'Northern Cyprus', geoCode: '2196', flag: '🇨🇾' }
    ],
    hu: [
      { code: 'HU', name: 'Hungary', geoCode: '2348', flag: '🇭🇺' }
    ]
  };

  // Comprehensive list of all world countries for manual selection
  const allWorldCountries = [
    { code: 'AF', name: 'Afghanistan', geoCode: '2004', flag: '🇦🇫' },
    { code: 'AL', name: 'Albania', geoCode: '2008', flag: '🇦🇱' },
    { code: 'DZ', name: 'Algeria', geoCode: '2012', flag: '🇩🇿' },
    { code: 'AD', name: 'Andorra', geoCode: '2020', flag: '🇦🇩' },
    { code: 'AO', name: 'Angola', geoCode: '2024', flag: '🇦🇴' },
    { code: 'AG', name: 'Antigua and Barbuda', geoCode: '2028', flag: '🇦🇬' },
    { code: 'AR', name: 'Argentina', geoCode: '2032', flag: '🇦🇷' },
    { code: 'AM', name: 'Armenia', geoCode: '2051', flag: '🇦🇲' },
    { code: 'AU', name: 'Australia', geoCode: '2036', flag: '🇦🇺' },
    { code: 'AT', name: 'Austria', geoCode: '2040', flag: '🇦🇹' },
    { code: 'AZ', name: 'Azerbaijan', geoCode: '2031', flag: '🇦🇿' },
    { code: 'BS', name: 'Bahamas', geoCode: '2044', flag: '🇧🇸' },
    { code: 'BH', name: 'Bahrain', geoCode: '2048', flag: '🇧🇭' },
    { code: 'BD', name: 'Bangladesh', geoCode: '2050', flag: '🇧🇩' },
    { code: 'BB', name: 'Barbados', geoCode: '2052', flag: '🇧🇧' },
    { code: 'BY', name: 'Belarus', geoCode: '2112', flag: '🇧🇾' },
    { code: 'BE', name: 'Belgium', geoCode: '2056', flag: '🇧🇪' },
    { code: 'BZ', name: 'Belize', geoCode: '2084', flag: '🇧🇿' },
    { code: 'BJ', name: 'Benin', geoCode: '2204', flag: '🇧🇯' },
    { code: 'BT', name: 'Bhutan', geoCode: '2064', flag: '🇧🇹' },
    { code: 'BO', name: 'Bolivia', geoCode: '2068', flag: '🇧🇴' },
    { code: 'BA', name: 'Bosnia and Herzegovina', geoCode: '2070', flag: '🇧🇦' },
    { code: 'BW', name: 'Botswana', geoCode: '2072', flag: '🇧🇼' },
    { code: 'BR', name: 'Brazil', geoCode: '2076', flag: '🇧🇷' },
    { code: 'BN', name: 'Brunei', geoCode: '2096', flag: '🇧🇳' },
    { code: 'BG', name: 'Bulgaria', geoCode: '2100', flag: '🇧🇬' },
    { code: 'BF', name: 'Burkina Faso', geoCode: '2854', flag: '🇧🇫' },
    { code: 'BI', name: 'Burundi', geoCode: '2108', flag: '🇧🇮' },
    { code: 'KH', name: 'Cambodia', geoCode: '2116', flag: '🇰🇭' },
    { code: 'CM', name: 'Cameroon', geoCode: '2120', flag: '🇨🇲' },
    { code: 'CA', name: 'Canada', geoCode: '2124', flag: '🇨🇦' },
    { code: 'CV', name: 'Cape Verde', geoCode: '2132', flag: '🇨🇻' },
    { code: 'CF', name: 'Central African Republic', geoCode: '2140', flag: '🇨🇫' },
    { code: 'TD', name: 'Chad', geoCode: '2148', flag: '🇹🇩' },
    { code: 'CL', name: 'Chile', geoCode: '2152', flag: '🇨🇱' },
    { code: 'CN', name: 'China', geoCode: '2156', flag: '🇨🇳' },
    { code: 'CO', name: 'Colombia', geoCode: '2170', flag: '🇨🇴' },
    { code: 'KM', name: 'Comoros', geoCode: '2174', flag: '🇰🇲' },
    { code: 'CG', name: 'Congo', geoCode: '2178', flag: '🇨🇬' },
    { code: 'CD', name: 'Congo (DRC)', geoCode: '2180', flag: '🇨🇩' },
    { code: 'CR', name: 'Costa Rica', geoCode: '2188', flag: '🇨🇷' },
    { code: 'HR', name: 'Croatia', geoCode: '2191', flag: '🇭🇷' },
    { code: 'CU', name: 'Cuba', geoCode: '2192', flag: '🇨🇺' },
    { code: 'CY', name: 'Cyprus', geoCode: '2196', flag: '🇨🇾' },
    { code: 'CZ', name: 'Czech Republic', geoCode: '2203', flag: '🇨🇿' },
    { code: 'DK', name: 'Denmark', geoCode: '2208', flag: '🇩🇰' },
    { code: 'DJ', name: 'Djibouti', geoCode: '2262', flag: '🇩🇯' },
    { code: 'DM', name: 'Dominica', geoCode: '2212', flag: '🇩🇲' },
    { code: 'DO', name: 'Dominican Republic', geoCode: '2214', flag: '🇩🇴' },
    { code: 'EC', name: 'Ecuador', geoCode: '2218', flag: '🇪🇨' },
    { code: 'EG', name: 'Egypt', geoCode: '2818', flag: '🇪🇬' },
    { code: 'SV', name: 'El Salvador', geoCode: '2222', flag: '🇸🇻' },
    { code: 'GQ', name: 'Equatorial Guinea', geoCode: '2226', flag: '🇬🇶' },
    { code: 'ER', name: 'Eritrea', geoCode: '2232', flag: '🇪🇷' },
    { code: 'EE', name: 'Estonia', geoCode: '2233', flag: '🇪🇪' },
    { code: 'ET', name: 'Ethiopia', geoCode: '2231', flag: '🇪🇹' },
    { code: 'FJ', name: 'Fiji', geoCode: '2242', flag: '🇫🇯' },
    { code: 'FI', name: 'Finland', geoCode: '2246', flag: '🇫🇮' },
    { code: 'FR', name: 'France', geoCode: '2250', flag: '🇫🇷' },
    { code: 'GA', name: 'Gabon', geoCode: '2266', flag: '🇬🇦' },
    { code: 'GM', name: 'Gambia', geoCode: '2270', flag: '🇬🇲' },
    { code: 'GE', name: 'Georgia', geoCode: '2268', flag: '🇬🇪' },
    { code: 'DE', name: 'Germany', geoCode: '2276', flag: '🇩🇪' },
    { code: 'GH', name: 'Ghana', geoCode: '2288', flag: '🇬🇭' },
    { code: 'GR', name: 'Greece', geoCode: '2300', flag: '🇬🇷' },
    { code: 'GD', name: 'Grenada', geoCode: '2308', flag: '🇬🇩' },
    { code: 'GT', name: 'Guatemala', geoCode: '2320', flag: '🇬🇹' },
    { code: 'GN', name: 'Guinea', geoCode: '2324', flag: '🇬🇳' },
    { code: 'GW', name: 'Guinea-Bissau', geoCode: '2624', flag: '🇬🇼' },
    { code: 'GY', name: 'Guyana', geoCode: '2328', flag: '🇬🇾' },
    { code: 'HT', name: 'Haiti', geoCode: '2332', flag: '🇭🇹' },
    { code: 'HN', name: 'Honduras', geoCode: '2340', flag: '🇭🇳' },
    { code: 'HK', name: 'Hong Kong', geoCode: '2344', flag: '🇭🇰' },
    { code: 'HU', name: 'Hungary', geoCode: '2348', flag: '🇭🇺' },
    { code: 'IS', name: 'Iceland', geoCode: '2352', flag: '🇮🇸' },
    { code: 'IN', name: 'India', geoCode: '2356', flag: '🇮🇳' },
    { code: 'ID', name: 'Indonesia', geoCode: '2360', flag: '🇮🇩' },
    { code: 'IR', name: 'Iran', geoCode: '2364', flag: '🇮🇷' },
    { code: 'IQ', name: 'Iraq', geoCode: '2368', flag: '🇮🇶' },
    { code: 'IE', name: 'Ireland', geoCode: '2372', flag: '🇮🇪' },
    { code: 'IL', name: 'Israel', geoCode: '2376', flag: '🇮🇱' },
    { code: 'IT', name: 'Italy', geoCode: '2380', flag: '🇮🇹' },
    { code: 'CI', name: 'Ivory Coast', geoCode: '2384', flag: '🇨🇮' },
    { code: 'JM', name: 'Jamaica', geoCode: '2388', flag: '🇯🇲' },
    { code: 'JP', name: 'Japan', geoCode: '2392', flag: '🇯🇵' },
    { code: 'JO', name: 'Jordan', geoCode: '2400', flag: '🇯🇴' },
    { code: 'KZ', name: 'Kazakhstan', geoCode: '2398', flag: '🇰🇿' },
    { code: 'KE', name: 'Kenya', geoCode: '2404', flag: '🇰🇪' },
    { code: 'KI', name: 'Kiribati', geoCode: '2296', flag: '🇰🇮' },
    { code: 'KW', name: 'Kuwait', geoCode: '2414', flag: '🇰🇼' },
    { code: 'KG', name: 'Kyrgyzstan', geoCode: '2417', flag: '🇰🇬' },
    { code: 'LA', name: 'Laos', geoCode: '2418', flag: '🇱🇦' },
    { code: 'LV', name: 'Latvia', geoCode: '2428', flag: '🇱🇻' },
    { code: 'LB', name: 'Lebanon', geoCode: '2422', flag: '🇱🇧' },
    { code: 'LS', name: 'Lesotho', geoCode: '2426', flag: '🇱🇸' },
    { code: 'LR', name: 'Liberia', geoCode: '2430', flag: '🇱🇷' },
    { code: 'LY', name: 'Libya', geoCode: '2434', flag: '🇱🇾' },
    { code: 'LI', name: 'Liechtenstein', geoCode: '2438', flag: '🇱🇮' },
    { code: 'LT', name: 'Lithuania', geoCode: '2440', flag: '🇱🇹' },
    { code: 'LU', name: 'Luxembourg', geoCode: '2442', flag: '🇱🇺' },
    { code: 'MO', name: 'Macau', geoCode: '2446', flag: '🇲🇴' },
    { code: 'MK', name: 'Macedonia', geoCode: '2807', flag: '🇲🇰' },
    { code: 'MG', name: 'Madagascar', geoCode: '2450', flag: '🇲🇬' },
    { code: 'MW', name: 'Malawi', geoCode: '2454', flag: '🇲🇼' },
    { code: 'MY', name: 'Malaysia', geoCode: '2458', flag: '🇲🇾' },
    { code: 'MV', name: 'Maldives', geoCode: '2462', flag: '🇲🇻' },
    { code: 'ML', name: 'Mali', geoCode: '2466', flag: '🇲🇱' },
    { code: 'MT', name: 'Malta', geoCode: '2470', flag: '🇲🇹' },
    { code: 'MH', name: 'Marshall Islands', geoCode: '2584', flag: '🇲🇭' },
    { code: 'MR', name: 'Mauritania', geoCode: '2478', flag: '🇲🇷' },
    { code: 'MU', name: 'Mauritius', geoCode: '2480', flag: '🇲🇺' },
    { code: 'MX', name: 'Mexico', geoCode: '2484', flag: '🇲🇽' },
    { code: 'FM', name: 'Micronesia', geoCode: '2583', flag: '🇫🇲' },
    { code: 'MD', name: 'Moldova', geoCode: '2498', flag: '🇲🇩' },
    { code: 'MC', name: 'Monaco', geoCode: '2492', flag: '🇲🇨' },
    { code: 'MN', name: 'Mongolia', geoCode: '2496', flag: '🇲🇳' },
    { code: 'ME', name: 'Montenegro', geoCode: '2499', flag: '🇲🇪' },
    { code: 'MA', name: 'Morocco', geoCode: '2504', flag: '🇲🇦' },
    { code: 'MZ', name: 'Mozambique', geoCode: '2508', flag: '🇲🇿' },
    { code: 'MM', name: 'Myanmar', geoCode: '2104', flag: '🇲🇲' },
    { code: 'NA', name: 'Namibia', geoCode: '2516', flag: '🇳🇦' },
    { code: 'NR', name: 'Nauru', geoCode: '2520', flag: '🇳🇷' },
    { code: 'NP', name: 'Nepal', geoCode: '2524', flag: '🇳🇵' },
    { code: 'NL', name: 'Netherlands', geoCode: '2528', flag: '🇳🇱' },
    { code: 'NZ', name: 'New Zealand', geoCode: '2554', flag: '🇳🇿' },
    { code: 'NI', name: 'Nicaragua', geoCode: '2558', flag: '🇳🇮' },
    { code: 'NE', name: 'Niger', geoCode: '2562', flag: '🇳🇪' },
    { code: 'NG', name: 'Nigeria', geoCode: '2566', flag: '🇳🇬' },
    { code: 'NO', name: 'Norway', geoCode: '2578', flag: '🇳🇴' },
    { code: 'OM', name: 'Oman', geoCode: '2512', flag: '🇴🇲' },
    { code: 'PK', name: 'Pakistan', geoCode: '2586', flag: '🇵🇰' },
    { code: 'PW', name: 'Palau', geoCode: '2585', flag: '🇵🇼' },
    { code: 'PS', name: 'Palestine', geoCode: '2275', flag: '🇵🇸' },
    { code: 'PA', name: 'Panama', geoCode: '2591', flag: '🇵🇦' },
    { code: 'PG', name: 'Papua New Guinea', geoCode: '2598', flag: '🇵🇬' },
    { code: 'PY', name: 'Paraguay', geoCode: '2600', flag: '🇵🇾' },
    { code: 'PE', name: 'Peru', geoCode: '2604', flag: '🇵🇪' },
    { code: 'PH', name: 'Philippines', geoCode: '2608', flag: '🇵🇭' },
    { code: 'PL', name: 'Poland', geoCode: '2616', flag: '🇵🇱' },
    { code: 'PT', name: 'Portugal', geoCode: '2620', flag: '🇵🇹' },
    { code: 'QA', name: 'Qatar', geoCode: '2634', flag: '🇶🇦' },
    { code: 'RO', name: 'Romania', geoCode: '2642', flag: '🇷🇴' },
    { code: 'RU', name: 'Russia', geoCode: '2643', flag: '🇷🇺' },
    { code: 'RW', name: 'Rwanda', geoCode: '2646', flag: '🇷🇼' },
    { code: 'KN', name: 'Saint Kitts and Nevis', geoCode: '2659', flag: '🇰🇳' },
    { code: 'LC', name: 'Saint Lucia', geoCode: '2662', flag: '🇱🇨' },
    { code: 'VC', name: 'Saint Vincent and the Grenadines', geoCode: '2670', flag: '🇻🇨' },
    { code: 'WS', name: 'Samoa', geoCode: '2882', flag: '🇼🇸' },
    { code: 'SM', name: 'San Marino', geoCode: '2674', flag: '🇸🇲' },
    { code: 'ST', name: 'Sao Tome and Principe', geoCode: '2678', flag: '🇸🇹' },
    { code: 'SA', name: 'Saudi Arabia', geoCode: '2682', flag: '🇸🇦' },
    { code: 'SN', name: 'Senegal', geoCode: '2686', flag: '🇸🇳' },
    { code: 'RS', name: 'Serbia', geoCode: '2688', flag: '🇷🇸' },
    { code: 'SC', name: 'Seychelles', geoCode: '2690', flag: '🇸🇨' },
    { code: 'SL', name: 'Sierra Leone', geoCode: '2694', flag: '🇸🇱' },
    { code: 'SG', name: 'Singapore', geoCode: '2702', flag: '🇸🇬' },
    { code: 'SK', name: 'Slovakia', geoCode: '2703', flag: '🇸🇰' },
    { code: 'SI', name: 'Slovenia', geoCode: '2705', flag: '🇸🇮' },
    { code: 'SB', name: 'Solomon Islands', geoCode: '2090', flag: '🇸🇧' },
    { code: 'SO', name: 'Somalia', geoCode: '2706', flag: '🇸🇴' },
    { code: 'ZA', name: 'South Africa', geoCode: '2710', flag: '🇿🇦' },
    { code: 'KR', name: 'South Korea', geoCode: '2410', flag: '🇰🇷' },
    { code: 'SS', name: 'South Sudan', geoCode: '2728', flag: '🇸🇸' },
    { code: 'ES', name: 'Spain', geoCode: '2724', flag: '🇪🇸' },
    { code: 'LK', name: 'Sri Lanka', geoCode: '2144', flag: '🇱🇰' },
    { code: 'SD', name: 'Sudan', geoCode: '2736', flag: '🇸🇩' },
    { code: 'SR', name: 'Suriname', geoCode: '2740', flag: '🇸🇷' },
    { code: 'SZ', name: 'Swaziland', geoCode: '2748', flag: '🇸🇿' },
    { code: 'SE', name: 'Sweden', geoCode: '2752', flag: '🇸🇪' },
    { code: 'CH', name: 'Switzerland', geoCode: '2756', flag: '🇨🇭' },
    { code: 'SY', name: 'Syria', geoCode: '2760', flag: '🇸🇾' },
    { code: 'TW', name: 'Taiwan', geoCode: '2158', flag: '🇹🇼' },
    { code: 'TJ', name: 'Tajikistan', geoCode: '2762', flag: '🇹🇯' },
    { code: 'TZ', name: 'Tanzania', geoCode: '2834', flag: '🇹🇿' },
    { code: 'TH', name: 'Thailand', geoCode: '2764', flag: '🇹🇭' },
    { code: 'TL', name: 'Timor-Leste', geoCode: '2626', flag: '🇹🇱' },
    { code: 'TG', name: 'Togo', geoCode: '2768', flag: '🇹🇬' },
    { code: 'TO', name: 'Tonga', geoCode: '2776', flag: '🇹🇴' },
    { code: 'TT', name: 'Trinidad and Tobago', geoCode: '2780', flag: '🇹🇹' },
    { code: 'TN', name: 'Tunisia', geoCode: '2788', flag: '🇹🇳' },
    { code: 'TR', name: 'Turkey', geoCode: '2792', flag: '🇹🇷' },
    { code: 'TM', name: 'Turkmenistan', geoCode: '2795', flag: '🇹🇲' },
    { code: 'TV', name: 'Tuvalu', geoCode: '2798', flag: '🇹🇻' },
    { code: 'UG', name: 'Uganda', geoCode: '2800', flag: '🇺🇬' },
    { code: 'UA', name: 'Ukraine', geoCode: '2804', flag: '🇺🇦' },
    { code: 'AE', name: 'United Arab Emirates', geoCode: '2784', flag: '🇦🇪' },
    { code: 'GB', name: 'United Kingdom', geoCode: '2826', flag: '🇬🇧' },
    { code: 'US', name: 'United States', geoCode: '2840', flag: '🇺🇸' },
    { code: 'UY', name: 'Uruguay', geoCode: '2858', flag: '🇺🇾' },
    { code: 'UZ', name: 'Uzbekistan', geoCode: '2860', flag: '🇺🇿' },
    { code: 'VU', name: 'Vanuatu', geoCode: '2548', flag: '🇻🇺' },
    { code: 'VA', name: 'Vatican City', geoCode: '2336', flag: '🇻🇦' },
    { code: 'VE', name: 'Venezuela', geoCode: '2862', flag: '🇻🇪' },
    { code: 'VN', name: 'Vietnam', geoCode: '2704', flag: '🇻🇳' },
    { code: 'YE', name: 'Yemen', geoCode: '2887', flag: '🇾🇪' },
    { code: 'ZM', name: 'Zambia', geoCode: '2894', flag: '🇿🇲' },
    { code: 'ZW', name: 'Zimbabwe', geoCode: '2716', flag: '🇿🇼' }
  ];

  const biddingStrategies = [
    { value: 'MANUAL_CPC', label: 'Manual CPC', description: 'You set your own maximum cost-per-click' },
    { value: 'MAXIMIZE_CLICKS', label: 'Maximize Clicks', description: 'Get the most clicks within your budget' },
    { value: 'TARGET_CPA', label: 'Target CPA', description: 'Automatically optimize for conversions' },
    { value: 'TARGET_ROAS', label: 'Target ROAS', description: 'Optimize for return on ad spend' },
    { value: 'MAXIMIZE_CONVERSIONS', label: 'Maximize Conversions', description: 'Get the most conversions within budget' },
    { value: 'MAXIMIZE_CONVERSION_VALUE', label: 'Maximize Conversion Value', description: 'Get the most conversion value' }
  ];

  const steps = [
    { number: 1, title: 'Campaign Info', icon: Target },
    { number: 2, title: 'Targeting', icon: Globe },
    { number: 3, title: 'Budget & Bidding', icon: DollarSign },
    { number: 4, title: 'Keywords & Ads', icon: Zap },
    { number: 5, title: 'Review & Create', icon: Rocket }
  ];

  const addKeyword = () => {
    if (keywordInput.trim()) {
      setKeywords([...keywords, { 
        text: keywordInput.trim(), 
        matchType: matchType,
        maxCpc: maxCpcBid 
      }]);
      setKeywordInput('');
    }
  };

  const removeKeyword = (index) => {
    setKeywords(keywords.filter((_, i) => i !== index));
  };

  const updateMultiAdGroup = (index, updates) => {
    setMultiAdGroups(prev => prev.map((group, i) => (
      i === index ? { ...group, ...updates } : group
    )));
  };

  const setMultiAdGroupCount = (count) => {
    const safeCount = Math.max(MIN_MULTI_AD_GROUPS, Math.min(MAX_MULTI_AD_GROUPS, Number(count) || MIN_MULTI_AD_GROUPS));
    setMultiAdGroups(prev => {
      if (safeCount <= prev.length) return prev.slice(0, safeCount);
      const next = [...prev];
      while (next.length < safeCount) {
        next.push(createEmptyAdGroup(next.length, {
          name: `${campaignName || 'Campaign'} - Ad Group ${next.length + 1}`,
          finalUrl
        }));
      }
      return next;
    });
  };

  const addMultiAdGroupKeyword = (groupIndex) => {
    const group = multiAdGroups[groupIndex];
    if (!group?.keywordInput?.trim()) return;
    updateMultiAdGroup(groupIndex, {
      keywords: [
        ...group.keywords,
        {
          text: group.keywordInput.trim(),
          matchType: group.matchType || 'BROAD',
          maxCpc: group.maxCpc || maxCpcBid
        }
      ],
      keywordInput: ''
    });
  };

  const removeMultiAdGroupKeyword = (groupIndex, keywordIndex) => {
    const group = multiAdGroups[groupIndex];
    if (!group) return;
    updateMultiAdGroup(groupIndex, {
      keywords: group.keywords.filter((_, i) => i !== keywordIndex)
    });
  };

  const updateMultiAdGroupHeadline = (groupIndex, headlineIndex, value) => {
    const group = multiAdGroups[groupIndex];
    if (!group) return;
    const updated = [...group.headlines];
    updated[headlineIndex] = value;
    updateMultiAdGroup(groupIndex, { headlines: updated });
  };

  const updateMultiAdGroupDescription = (groupIndex, descriptionIndex, value) => {
    const group = multiAdGroups[groupIndex];
    if (!group) return;
    const updated = [...group.descriptions];
    updated[descriptionIndex] = value;
    updateMultiAdGroup(groupIndex, { descriptions: updated });
  };

  const addMultiAdGroupHeadline = (groupIndex) => {
    const group = multiAdGroups[groupIndex];
    if (!group || group.headlines.length >= 15) return;
    updateMultiAdGroup(groupIndex, { headlines: [...group.headlines, ''] });
  };

  const addMultiAdGroupDescription = (groupIndex) => {
    const group = multiAdGroups[groupIndex];
    if (!group || group.descriptions.length >= 4) return;
    updateMultiAdGroup(groupIndex, { descriptions: [...group.descriptions, ''] });
  };

  const addHeadline = () => {
    if (headlines.length < 15) {
      setHeadlines([...headlines, '']);
    }
  };

  const updateHeadline = (index, value) => {
    const updated = [...headlines];
    updated[index] = value;
    setHeadlines(updated);
  };

  const removeHeadline = (index) => {
    if (headlines.length > 3) {
      setHeadlines(headlines.filter((_, i) => i !== index));
    }
  };

  const addDescription = () => {
    if (descriptions.length < 4) {
      setDescriptions([...descriptions, '']);
    }
  };

  const updateDescription = (index, value) => {
    const updated = [...descriptions];
    updated[index] = value;
    setDescriptions(updated);
  };

  const removeDescription = (index) => {
    if (descriptions.length > 2) {
      setDescriptions(descriptions.filter((_, i) => i !== index));
    }
  };

  const generateAdsWithAI = async () => {
    setAiGenerating(true);
    
    try {
      const keywordForAds = keywords[0]?.text || campaignName || 'product';
      
      // Get auth token
      const token = await getToken();
      
      const response = await fetch(`${API_URL}/ads/generate-multilang`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          keyword: keywordForAds,
          languages: [language],
          businessInfo,
          landingPageUrl: landingPageUrl || businessInfo.website || null,
          analyzeCompetitors,
          descriptionTypes
        })
      });

      const data = await response.json();
      if (data.success && data.data[language]) {
        const adData = data.data[language];
        
        // Auto-populate headlines and descriptions
        setHeadlines(adData.headlines.slice(0, 15));
        setDescriptions(adData.descriptions.slice(0, 4));
        
        setShowAiModal(false);
        
        // Show success message
        alert(`✨ Generated ${adData.headlines.length} headlines and ${adData.descriptions.length} descriptions with AI!`);
      }
    } catch (error) {
      console.error('Error generating ads with AI:', error);
      alert('Failed to generate ads. Please try again.');
    } finally {
      setAiGenerating(false);
    }
  };

  const fetchAdvice = async () => {
    setAdviceLoading(true);
    setAdviceError(null);
    try {
      const data = await apiCallJson('/api/campaigns/advice', {
        method: 'POST',
        timeout: 45000,
        body: { goal: advisorGoal, dailyBudget: budgetAmount }
      });
      if (data.success) {
        setAdvice(data);
      } else {
        setAdviceError(data.error || 'Could not generate advice');
      }
    } catch (error) {
      setAdviceError(error.message || 'Could not generate advice');
    } finally {
      setAdviceLoading(false);
    }
  };

  const applyBiddingAdvice = () => {
    const b = advice?.advice?.biddingStrategy;
    if (!b) return;
    setBiddingStrategy(b.recommended);
    if (b.recommended === 'TARGET_CPA' && b.targetCpa) setTargetCpa(b.targetCpa);
    if (b.recommended === 'TARGET_ROAS' && b.targetRoas) setTargetRoas(b.targetRoas);
  };

  const createCampaign = async () => {
    if (!campaignName.trim()) {
      setCreateResult({ success: false, error: 'Campaign name is required. Please go back to Step 1 and enter a name.' });
      return;
    }

    setCreating(true);
    setCreateResult(null);

    try {
      const payload = {
        campaignType,
        campaignName: campaignName.trim(),
        adGroupName: adGroupName || `${campaignName} - Ad Group 1`,
        adGroups: isMultipleAdGroupMode ? multiAdGroups.map((group, index) => ({
          name: group.name || `${campaignName} - Ad Group ${index + 1}`,
          keywords: group.keywords.map(k => ({
            keyword: k.text,
            matchType: k.matchType,
            maxCpc: k.maxCpc
          })),
          ads: {
            headlines: group.headlines.filter(h => h.trim()),
            descriptions: group.descriptions.filter(d => d.trim()),
            finalUrl: group.finalUrl,
            path1: group.path1 || undefined,
            path2: group.path2 || undefined
          },
          maxCpcBid: group.maxCpc || maxCpcBid
        })) : undefined,
        budget: budgetAmount,
        budgetType,
        deliveryMethod,
        biddingStrategy,
        targetCpa: biddingStrategy === 'TARGET_CPA' ? targetCpa : undefined,
        targetRoas: biddingStrategy === 'TARGET_ROAS' ? targetRoas : undefined,
        networkSettings,
        status: campaignStatus,
        language,
        country: availableLanguages.find(l => l.code === language)?.country || 'NL',
        countries: selectedCountries.map(c => c.geoCode),
        startDate: startDate || undefined,
        endDate: endDate || undefined,
        containsEuPolitical,
        // Keywords (Search only)
        keywords: campaignType === 'SEARCH' && !isMultipleAdGroupMode ? keywords.map(k => ({
          keyword: k.text,
          matchType: k.matchType,
          maxCpc: k.maxCpc
        })) : [],
        ads: {
          headlines: headlines.filter(h => h.trim()),
          descriptions: descriptions.filter(d => d.trim()),
          finalUrl,
          path1: path1 || undefined,
          path2: path2 || undefined
        },
        maxCpcBid,
        sitelinks: sitelinks.length > 0 ? sitelinks : undefined,
        callouts: callouts.length > 0 ? callouts : undefined,
        structuredSnippets: structuredSnippets.length > 0 ? structuredSnippets : undefined,
        // NEW: Assets for Performance Max / Demand Gen / Display
        assetGroupName: campaignType === 'PERFORMANCE_MAX' ? (assetGroupName || `${campaignName} - Asset Group 1`) : undefined,
        imageUrls: (campaignType !== 'SEARCH') ? imageUrls : undefined,
        finalUrl: (campaignType !== 'SEARCH') ? finalUrl : undefined,
        businessName: (campaignType !== 'SEARCH') ? businessName : undefined,
        callToAction: (campaignType === 'DEMAND_GEN') ? callToAction : undefined,
        longHeadline: (campaignType === 'DISPLAY') ? longHeadline : undefined
      };

      // Get auth token
      const token = await getToken();
      
      // Get selected account from localStorage
      const savedAccount = localStorage.getItem('selectedGoogleAdsAccount');
      let accountId = null;
      if (savedAccount) {
        try {
          const account = JSON.parse(savedAccount);
          accountId = account.id;
          console.log('📊 Using account:', accountId);
        } catch (e) {
          console.error('Failed to parse saved account:', e);
        }
      }
      
      if (!accountId) {
        throw new Error('Please select a Google Ads account first');
      }
      
      const response = await fetch(`${API_URL}/campaigns/create-complete`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'X-Account-Id': accountId
        },
        body: JSON.stringify(payload)
      });

      // Check if response is OK
      if (!response.ok) {
        const text = await response.text();
        console.error('Server error:', text);
        // Try to parse JSON error response for detailed message
        try {
          const errorData = JSON.parse(text);
          setCreateResult({
            success: false,
            error: errorData.error || `Server returned ${response.status}`,
            details: errorData.details || [],
            apiLimited: errorData.apiLimited || false
          });
          return;
        } catch (parseErr) {
          throw new Error(`Server returned ${response.status}: ${text || response.statusText}`);
        }
      }

      const data = await response.json();
      setCreateResult(data);

      if (data.success) {
        console.log('✅ Campaign created successfully:', data);
      }
    } catch (error) {
      console.error('Error creating campaign:', error);
      setCreateResult({
        success: false,
        error: error.message
      });
    } finally {
      setCreating(false);
    }
  };

  const publishCampaign = async () => {
    setPublishing(true);
    setPublishResult(null);
    
    try {
      // Use the draft campaign ID from cloned campaign or create result
      const campaignId = initialData?.id || draftCampaignId;
      
      if (!campaignId) {
        throw new Error('No campaign ID available for publishing');
      }
      
      console.log('📤 Publishing campaign:', campaignId);
      
      // Get auth token
      const token = await getToken();
      
      // Get selected account from localStorage
      const savedAccount = localStorage.getItem('selectedGoogleAdsAccount');
      let accountId = null;
      if (savedAccount) {
        try {
          const account = JSON.parse(savedAccount);
          accountId = account.id;
        } catch (e) {
          console.error('Failed to parse saved account:', e);
        }
      }
      
      if (!accountId) {
        throw new Error('Please select a Google Ads account first');
      }
      
      const response = await fetch(`${API_URL}/campaigns/publish`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'X-Account-Id': accountId
        },
        body: JSON.stringify({
          campaignId,
          customerId: accountId
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setPublishResult(data);
        console.log('✅ Campaign published:', data.googleAdsId);
      } else {
        throw new Error(data.error || 'Publishing failed');
      }
    } catch (error) {
      console.error('❌ Error publishing campaign:', error);
      setPublishResult({
        success: false,
        error: error.message
      });
    } finally {
      setPublishing(false);
    }
  };

  // Auto-select first country when language changes
  useEffect(() => {
    if (language && languageCountries[language]) {
      if (selectedCountries.length === 0) {
        setSelectedCountries([languageCountries[language][0]]);
      }
    }
  }, [language]);

  const removeCountry = (countryToRemove) => {
    if (selectedCountries.length === 1) return; // Must have at least 1
    setSelectedCountries(prev => prev.filter(c => c.code !== countryToRemove.code));
  };

  const addExtraCountry = (country) => {
    if (selectedCountries.some(c => c.code === country.code)) {
      return; // Already selected
    }
    setSelectedCountries(prev => [...prev, country]);
    setExtraCountrySearch('');
    setShowExtraCountryDropdown(false);
  };

  const filteredExtraCountries = extraCountrySearch.length > 0
    ? allWorldCountries.filter(country =>
        country.name.toLowerCase().includes(extraCountrySearch.toLowerCase()) &&
        !selectedCountries.some(c => c.code === country.code)
      ).slice(0, 10)
    : [];

  const openCountryModal = () => {
    setTempSelectedCountries([...selectedCountries]);
    setShowCountryModal(true);
  };

  const closeCountryModal = () => {
    setShowCountryModal(false);
  };

  const applyCountrySelection = () => {
    setSelectedCountries([...tempSelectedCountries]);
    setShowCountryModal(false);
  };

  const toggleTempCountrySelection = (country) => {
    setTempSelectedCountries(prev => {
      const isSelected = prev.some(c => c.code === country.code);
      if (isSelected) {
        // Must have at least one country selected
        if (prev.length === 1) return prev;
        return prev.filter(c => c.code !== country.code);
      } else {
        return [...prev, country];
      }
    });
  };

  const toggleAllTempCountries = () => {
    const availableCountries = languageCountries[language] || [];
    if (tempSelectedCountries.length === availableCountries.length) {
      // Deselect all except first
      setTempSelectedCountries([availableCountries[0]]);
    } else {
      // Select all
      setTempSelectedCountries([...availableCountries]);
    }
  };

  const canProceedToNextStep = () => {
    switch(currentStep) {
      case 1:
        return campaignName.trim().length > 0;
      case 2:
        return language !== '' && selectedCountries.length > 0;
      case 3:
        return budgetAmount > 0;
      case 4:
        // Different requirements per campaign type
        if (campaignType === 'SEARCH') {
          return keywords.length > 0 && headlines.filter(h => h.trim()).length >= 3 && descriptions.filter(d => d.trim()).length >= 2;
        } else if (campaignType === 'PERFORMANCE_MAX') {
          return headlines.filter(h => h.trim()).length >= 3 && descriptions.filter(d => d.trim()).length >= 2 && finalUrl.trim().length > 0;
        } else if (campaignType === 'DEMAND_GEN') {
          return headlines.filter(h => h.trim()).length >= 2 && descriptions.filter(d => d.trim()).length >= 2 && (imageUrls.landscape.length > 0 || imageUrls.square.length > 0);
        } else if (campaignType === 'DISPLAY') {
          return headlines.filter(h => h.trim()).length >= 3 && descriptions.filter(d => d.trim()).length >= 2 && (imageUrls.landscape.length > 0 || imageUrls.square.length > 0);
        }
        return true;
      default:
        return true;
    }
  };

  // Render Step Content
  const renderStepContent = () => {
    switch(currentStep) {
      case 1:
        return renderCampaignInfo();
      case 2:
        return renderTargeting();
      case 3:
        return renderBudgetBidding();
      case 4:
        // Different content based on campaign type
        if (campaignType === 'SEARCH') {
          return renderKeywordsAds();
        } else if (campaignType === 'PERFORMANCE_MAX') {
          return renderPerformanceMaxAssets();
        } else if (campaignType === 'DEMAND_GEN') {
          return renderDemandGenAssets();
        } else if (campaignType === 'DISPLAY') {
          return renderDisplayAssets();
        }
        return renderKeywordsAds(); // fallback
      case 5:
        return renderReview();
      default:
        return null;
    }
  };

  const renderCampaignInfo = () => (
    <div style={styles.stepContent}>
      <h3 style={styles.stepTitle}>
        <Target size={24} style={{ color: '#8b5cf6', marginRight: 12 }} />
        Campaign Information
      </h3>
      <p style={styles.stepDescription}>
        Set up the basic information for your campaign
      </p>

      {/* Campaign Advisor */}
      <div style={{
        background: 'rgba(139, 92, 246, 0.08)',
        border: '1px solid rgba(139, 92, 246, 0.3)',
        borderRadius: 12,
        padding: 20,
        marginBottom: 28
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
          <Sparkles size={18} style={{ color: '#a78bfa' }} />
          <span style={{ color: 'var(--text-primary)', fontWeight: 600, fontSize: 15 }}>Campaign Advisor</span>
        </div>
        <p style={{ color: 'var(--text-secondary)', fontSize: 13, margin: '0 0 14px' }}>
          Get advice on campaign type, bidding strategy and budget, based on your account's performance over the last 30 days.
        </p>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <select
            value={advisorGoal}
            onChange={(e) => setAdvisorGoal(e.target.value)}
            style={{ ...styles.select, width: 'auto', minWidth: 220 }}
          >
            <option value="leads">🎯 Goal: Leads / sign-ups</option>
            <option value="sales">🛒 Goal: Sales / e-commerce</option>
            <option value="traffic">🚦 Goal: Website traffic</option>
            <option value="awareness">📣 Goal: Brand awareness</option>
          </select>
          <button
            onClick={fetchAdvice}
            disabled={adviceLoading}
            style={{
              background: 'linear-gradient(135deg, #8b5cf6, #6366f1)',
              border: 'none',
              borderRadius: 8,
              color: 'white',
              padding: '10px 18px',
              fontSize: 14,
              fontWeight: 600,
              cursor: adviceLoading ? 'wait' : 'pointer',
              opacity: adviceLoading ? 0.7 : 1,
              display: 'flex',
              alignItems: 'center',
              gap: 8
            }}
          >
            {adviceLoading ? <RefreshCw size={15} className="spin" /> : <Sparkles size={15} />}
            {adviceLoading ? 'Analyzing account…' : 'Get advice'}
          </button>
        </div>

        {adviceError && (
          <div style={{ marginTop: 12, color: '#fca5a5', fontSize: 13 }}>⚠️ {adviceError}</div>
        )}

        {advice?.advice && (
          <div style={{ marginTop: 16, display: 'grid', gap: 10 }}>
            {advice.accountStats?.last30Days && (
              <div style={{ color: 'var(--text-tertiary)', fontSize: 12 }}>
                Last 30 days: {advice.accountStats.last30Days.conversions} conversions
                {advice.accountStats.last30Days.cpa != null && <> · CPA €{advice.accountStats.last30Days.cpa}</>}
                {advice.accountStats.last30Days.roas != null && <> · ROAS {advice.accountStats.last30Days.roas}x</>}
                {advice.accountStats.last30Days.avgCpc != null && <> · avg. CPC €{advice.accountStats.last30Days.avgCpc}</>}
              </div>
            )}

            {/* Campaign type advice */}
            <div style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-primary)', borderRadius: 8, padding: '12px 14px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <span style={{ color: 'var(--text-primary)', fontSize: 13, fontWeight: 600 }}>
                  Campaign type: {advice.advice.campaignType.recommended.replace(/_/g, ' ')}
                </span>
                {campaignType !== advice.advice.campaignType.recommended && (
                  <button
                    onClick={() => setCampaignType(advice.advice.campaignType.recommended)}
                    style={{ background: 'rgba(139,92,246,0.25)', border: '1px solid rgba(139,92,246,0.5)', borderRadius: 6, color: 'var(--brand-primary)', padding: '4px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
                  >
                    Apply
                  </button>
                )}
              </div>
              <div style={{ color: 'var(--text-secondary)', fontSize: 12, marginTop: 6, lineHeight: 1.5 }}>
                {advice.advice.campaignType.reason}
              </div>
            </div>

            {/* Bidding strategy advice */}
            <div style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-primary)', borderRadius: 8, padding: '12px 14px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <span style={{ color: 'var(--text-primary)', fontSize: 13, fontWeight: 600 }}>
                  Bidding: {advice.advice.biddingStrategy.recommended.replace(/_/g, ' ')}
                  {advice.advice.biddingStrategy.targetCpa ? ` (€${advice.advice.biddingStrategy.targetCpa})` : ''}
                  {advice.advice.biddingStrategy.targetRoas ? ` (${advice.advice.biddingStrategy.targetRoas}%)` : ''}
                </span>
                {biddingStrategy !== advice.advice.biddingStrategy.recommended && (
                  <button
                    onClick={applyBiddingAdvice}
                    style={{ background: 'rgba(139,92,246,0.25)', border: '1px solid rgba(139,92,246,0.5)', borderRadius: 6, color: 'var(--brand-primary)', padding: '4px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
                  >
                    Apply
                  </button>
                )}
              </div>
              <div style={{ color: 'var(--text-secondary)', fontSize: 12, marginTop: 6, lineHeight: 1.5 }}>
                {advice.advice.biddingStrategy.reason}
              </div>
            </div>

            {/* Budget advice */}
            {advice.advice.budget && (
              <div style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-primary)', borderRadius: 8, padding: '12px 14px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                  <span style={{ color: advice.advice.budget.status === 'low' ? '#fbbf24' : '#34d399', fontSize: 13, fontWeight: 600 }}>
                    Budget: {advice.advice.budget.status === 'low' ? 'too low for your CPC' : 'looks good'}
                  </span>
                  {advice.advice.budget.status === 'low' && advice.advice.budget.minRecommended && (
                    <button
                      onClick={() => setBudgetAmount(advice.advice.budget.minRecommended)}
                      style={{ background: 'rgba(139,92,246,0.25)', border: '1px solid rgba(139,92,246,0.5)', borderRadius: 6, color: 'var(--brand-primary)', padding: '4px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
                    >
                      Set €{advice.advice.budget.minRecommended}/day
                    </button>
                  )}
                </div>
                <div style={{ color: 'var(--text-secondary)', fontSize: 12, marginTop: 6, lineHeight: 1.5 }}>
                  {advice.advice.budget.reason}
                </div>
              </div>
            )}

            {/* Match type advice */}
            <div style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-primary)', borderRadius: 8, padding: '12px 14px' }}>
              <span style={{ color: 'var(--text-primary)', fontSize: 13, fontWeight: 600 }}>
                Match type: start with {advice.advice.matchType.recommended}
              </span>
              <div style={{ color: 'var(--text-secondary)', fontSize: 12, marginTop: 6, lineHeight: 1.5 }}>
                {advice.advice.matchType.reason}
              </div>
            </div>

            {/* Tips */}
            {(advice.advice.tips || []).length > 0 && (
              <div style={{ color: 'var(--text-tertiary)', fontSize: 12, lineHeight: 1.7 }}>
                {advice.advice.tips.map((tip, i) => (
                  <div key={i}>💡 {tip}</div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      <div style={styles.formGroup}>
        <label style={styles.label}>
          Campaign Type <span style={{ color: '#ef4444' }}>*</span>
        </label>
        <select
          value={campaignType}
          onChange={(e) => {
            setCampaignType(e.target.value);
            // Reset certain fields when changing campaign type
            if (e.target.value !== 'SEARCH') {
              setKeywords([]); // Non-search campaigns don't use keywords
              setAdGroupMode('single');
            }
          }}
          style={styles.select}
        >
          <option value="SEARCH">🔍 Search Network</option>
          <option value="PERFORMANCE_MAX">🚀 Performance Max (Pmax) — Beta</option>
          <option value="DEMAND_GEN">📱 Demand Gen (YouTube, Gmail, Discover)</option>
          <option value="DISPLAY">🖼️ Display Network</option>
        </select>
        <div style={styles.hint}>
          {campaignType === 'SEARCH' && '💡 Text ads that appear in Google search results'}
          {campaignType === 'PERFORMANCE_MAX' && '⚠️ Beta: campaign, asset group and text assets are created for you (paused). Images and a logo must be added in the Google Ads UI before the campaign can run.'}
          {campaignType === 'DEMAND_GEN' && '💡 Image/video ads on YouTube, Gmail, and Discover'}
          {campaignType === 'DISPLAY' && '💡 Banner ads across millions of websites'}
        </div>
      </div>

      <div style={styles.formGroup}>
        <label style={styles.label}>
          Campaign Name <span style={{ color: '#ef4444' }}>*</span>
        </label>
        <input
          type="text"
          value={campaignName}
          onChange={(e) => setCampaignName(e.target.value)}
          placeholder="e.g., Summer Sale 2024"
          style={styles.input}
        />
        <div style={styles.hint}>
          💡 Choose a descriptive name that clearly identifies this campaign
        </div>
      </div>

      {campaignType === 'SEARCH' && (
        <div style={styles.formGroup}>
          <label style={styles.label}>Ad Group Setup</label>
          <div style={styles.buttonGroup}>
            {[
              { value: 'single', label: 'Single ad group', desc: 'Current fast setup' },
              { value: 'multiple', label: 'Multiple ad groups', desc: 'Advanced manual setup' }
            ].map(option => (
              <button
                key={option.value}
                type="button"
                onClick={() => setAdGroupMode(option.value)}
                style={{
                  ...styles.optionButton,
                  ...(adGroupMode === option.value ? styles.optionButtonActive : {})
                }}
              >
                <div style={{ fontWeight: 600, marginBottom: 4 }}>{option.label}</div>
                <div style={{ fontSize: 11, opacity: 0.7 }}>{option.desc}</div>
              </button>
            ))}
          </div>
          <div style={styles.hint}>
            Multiple ad groups only loads extra fields after you choose it, so the normal setup stays lightweight.
          </div>
        </div>
      )}

      {campaignType !== 'SEARCH' || adGroupMode === 'single' ? (
        <div style={styles.formGroup}>
          <label style={styles.label}>Ad Group Name (Optional)</label>
          <input
            type="text"
            value={adGroupName}
            onChange={(e) => setAdGroupName(e.target.value)}
            placeholder={`${campaignName || 'Campaign'} - Ad Group 1`}
            style={styles.input}
          />
          <div style={styles.hint}>
            Leave empty to auto-generate
          </div>
        </div>
      ) : (
        <div style={styles.card}>
          <h4 style={styles.cardTitle}>Multiple Ad Groups</h4>
          <div style={styles.formGroup}>
            <label style={styles.label}>Number of ad groups</label>
            <select
              value={multiAdGroups.length}
              onChange={(e) => setMultiAdGroupCount(e.target.value)}
              style={styles.select}
            >
              {Array.from({ length: MAX_MULTI_AD_GROUPS - MIN_MULTI_AD_GROUPS + 1 }, (_, i) => i + MIN_MULTI_AD_GROUPS).map(count => (
                <option key={count} value={count}>{count} ad groups</option>
              ))}
            </select>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
            {multiAdGroups.map((group, index) => (
              <div key={group.id} style={{
                padding: 14,
                borderRadius: 10,
                border: '1px solid rgba(255,255,255,0.12)',
                background: 'rgba(255,255,255,0.04)'
              }}>
                <label style={styles.label}>Ad Group {index + 1}</label>
                <input
                  type="text"
                  value={group.name}
                  onChange={(e) => updateMultiAdGroup(index, { name: e.target.value })}
                  placeholder={`${campaignName || 'Campaign'} - Ad Group ${index + 1}`}
                  style={styles.input}
                />
              </div>
            ))}
          </div>
        </div>
      )}

      <div style={styles.formGroup}>
        <label style={styles.label}>Campaign Status</label>
        <div style={styles.radioGroup}>
          {['PAUSED', 'ENABLED'].map(status => (
            <label key={status} style={styles.radioLabel}>
              <input
                type="radio"
                value={status}
                checked={campaignStatus === status}
                onChange={(e) => setCampaignStatus(e.target.value)}
                style={styles.radio}
              />
              <span>{status === 'PAUSED' ? '⏸️ Paused' : '✅ Enabled'}</span>
              <div style={styles.radioHint}>
                {status === 'PAUSED' 
                  ? 'Recommended: Review before activating' 
                  : 'Campaign will start serving immediately'}
              </div>
            </label>
          ))}
        </div>
      </div>
    </div>
  );

  const renderBudgetBidding = () => (
    <div style={styles.stepContent}>
      <h3 style={styles.stepTitle}>
        <DollarSign size={24} style={{ color: '#10b981', marginRight: 12 }} />
        Budget & Bidding Strategy
      </h3>
      <p style={styles.stepDescription}>
        Configure how much you want to spend and how to bid
      </p>

      {advice?.advice?.biddingStrategy && biddingStrategy !== advice.advice.biddingStrategy.recommended && (
        <div style={{
          background: 'rgba(139, 92, 246, 0.08)',
          border: '1px solid rgba(139, 92, 246, 0.3)',
          borderRadius: 10,
          padding: '12px 16px',
          marginBottom: 20,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 12,
          flexWrap: 'wrap'
        }}>
          <span style={{ color: 'var(--text-secondary)', fontSize: 13 }}>
            <Sparkles size={14} style={{ color: '#a78bfa', verticalAlign: -2, marginRight: 6 }} />
            Advisor recommends <strong>{advice.advice.biddingStrategy.recommended.replace(/_/g, ' ')}</strong>
            {advice.advice.biddingStrategy.targetCpa ? ` with a €${advice.advice.biddingStrategy.targetCpa} target CPA` : ''}
            {advice.advice.biddingStrategy.targetRoas ? ` with a ${advice.advice.biddingStrategy.targetRoas}% target ROAS` : ''}
          </span>
          <button
            onClick={applyBiddingAdvice}
            style={{ background: 'rgba(139,92,246,0.25)', border: '1px solid rgba(139,92,246,0.5)', borderRadius: 6, color: 'var(--brand-primary)', padding: '5px 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
          >
            Apply
          </button>
        </div>
      )}

      {/* Budget */}
      <div style={styles.card}>
        <h4 style={styles.cardTitle}>Budget Settings</h4>
        
        <div style={styles.formGroup}>
          <label style={styles.label}>Budget Type</label>
          <div style={styles.buttonGroup}>
            {[
              { value: 'daily', label: '📅 Daily Budget', desc: 'Average daily spend' },
              { value: 'total', label: '💰 Total Budget', desc: 'Campaign lifetime' }
            ].map(option => (
              <button
                key={option.value}
                onClick={() => setBudgetType(option.value)}
                style={{
                  ...styles.optionButton,
                  ...(budgetType === option.value ? styles.optionButtonActive : {})
                }}
              >
                <div style={{ fontWeight: 600, marginBottom: 4 }}>{option.label}</div>
                <div style={{ fontSize: 11, opacity: 0.7 }}>{option.desc}</div>
              </button>
            ))}
          </div>
        </div>

        <div style={styles.formGroup}>
          <label style={styles.label}>
            Budget Amount <span style={{ color: '#ef4444' }}>*</span>
          </label>
          <div style={styles.inputGroup}>
            <span style={styles.inputPrefix}>€</span>
            <input
              type="number"
              value={budgetAmount}
              onChange={(e) => setBudgetAmount(parseFloat(e.target.value) || 0)}
              min="0.01"
              step="0.50"
              style={{ ...styles.input, paddingLeft: 40 }}
            />
          </div>
          <div style={styles.hint}>
            {budgetType === 'daily' 
              ? `Monthly estimate: €${formatNumber(budgetAmount * 30.4, 2)}`
              : 'Total budget for the entire campaign'}
          </div>
        </div>

        <div style={styles.formGroup}>
          <label style={styles.label}>Delivery Method</label>
          <select
            value={deliveryMethod}
            onChange={(e) => setDeliveryMethod(e.target.value)}
            style={styles.select}
          >
            <option value="STANDARD">Standard - Spread budget evenly throughout the day</option>
            <option value="ACCELERATED">Accelerated - Spend budget as quickly as possible</option>
          </select>
        </div>
      </div>

      {/* Bidding Strategy */}
      <div style={{ ...styles.card, marginTop: 20 }}>
        <h4 style={styles.cardTitle}>Bidding Strategy</h4>
        
        <div style={styles.formGroup}>
          <label style={styles.label}>Strategy Type</label>
          <select
            value={biddingStrategy}
            onChange={(e) => setBiddingStrategy(e.target.value)}
            style={styles.select}
          >
            {biddingStrategies.map(strategy => (
              <option key={strategy.value} value={strategy.value}>
                {strategy.label} - {strategy.description}
              </option>
            ))}
          </select>
        </div>

        {biddingStrategy === 'TARGET_CPA' && (
          <div style={styles.formGroup}>
            <label style={styles.label}>Target CPA</label>
            <div style={styles.inputGroup}>
              <span style={styles.inputPrefix}>€</span>
              <input
                type="number"
                value={targetCpa}
                onChange={(e) => setTargetCpa(parseFloat(e.target.value) || 0)}
                min="0.01"
                step="0.50"
                style={{ ...styles.input, paddingLeft: 40 }}
              />
            </div>
            <div style={styles.hint}>
              Google will optimize to get conversions at this average cost
            </div>
          </div>
        )}

        {biddingStrategy === 'TARGET_ROAS' && (
          <div style={styles.formGroup}>
            <label style={styles.label}>Target ROAS (%)</label>
            <input
              type="number"
              value={targetRoas}
              onChange={(e) => setTargetRoas(parseFloat(e.target.value) || 0)}
              min="1"
              step="10"
              style={styles.input}
            />
            <div style={styles.hint}>
              Target return on ad spend (e.g., 400% = €4 revenue for every €1 spent)
            </div>
          </div>
        )}

        {biddingStrategy === 'MANUAL_CPC' && (
          <div style={styles.formGroup}>
            <label style={styles.label}>Default Max CPC Bid</label>
            <div style={styles.inputGroup}>
              <span style={styles.inputPrefix}>€</span>
              <input
                type="number"
                value={maxCpcBid}
                onChange={(e) => setMaxCpcBid(parseFloat(e.target.value) || 0)}
                min="0.01"
                step="0.10"
                style={{ ...styles.input, paddingLeft: 40 }}
              />
            </div>
            <div style={styles.hint}>
              Maximum amount you'll pay per click
            </div>
          </div>
        )}
      </div>
    </div>
  );

  const renderTargeting = () => (
    <div style={styles.stepContent}>
      <h3 style={styles.stepTitle}>
        <Globe size={24} style={{ color: '#3b82f6', marginRight: 12 }} />
        Targeting & Networks
      </h3>
      <p style={styles.stepDescription}>
        Choose where and to whom your ads will show
      </p>

      {/* Language & Location */}
      <div style={styles.card}>
        <h4 style={styles.cardTitle}>Language & Location</h4>
        
        <div style={styles.formGroup}>
          <label style={styles.label}>
            Target Language <span style={{ color: '#ef4444' }}>*</span>
          </label>
          <div style={styles.languageGrid}>
            {availableLanguages.map(lang => (
              <button
                key={lang.code}
                onClick={() => setLanguage(lang.code)}
                style={{
                  ...styles.languageButton,
                  ...(language === lang.code ? styles.languageButtonActive : {})
                }}
              >
                <span style={{ fontSize: 24, marginBottom: 8 }}>{lang.flag}</span>
                <div style={{ fontWeight: 600, marginBottom: 2 }}>{lang.name}</div>
                <div style={{ fontSize: 11, opacity: 0.7 }}>{lang.country}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Country Selection */}
        {language && languageCountries[language] && (
          <div style={styles.formGroup}>
            <label style={styles.label}>
              Target Locations <span style={{ color: '#ef4444' }}>*</span>
              <span style={{ color: 'rgba(255,255,255,0.6)', fontSize: 13, fontWeight: 400, marginLeft: 8 }}>
                (cities and/or countries)
              </span>
            </label>
            
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              {/* Selected locations (cities/countries) as badges with X button */}
              {selectedCountries.map(country => (
                <div
                  key={country.code}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    padding: '8px 14px',
                    background: 'rgba(16,185,129,0.15)',
                    border: '2px solid rgba(16,185,129,0.4)',
                    borderRadius: 12,
                    color: '#10b981',
                    fontSize: 14,
                    fontWeight: 500
                  }}
                >
                  <span style={{ fontSize: 18 }}>{country.flag}</span>
                  <span>{country.name}</span>
                  {country.type === 'city' && <span style={{ fontSize: 11, opacity: 0.7 }}>(city)</span>}
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      removeCountry(country);
                    }}
                    disabled={selectedCountries.length === 1}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: selectedCountries.length === 1 ? 'rgba(255,255,255,0.3)' : '#10b981',
                      cursor: selectedCountries.length === 1 ? 'not-allowed' : 'pointer',
                      padding: 0,
                      marginLeft: 4,
                      display: 'flex',
                      alignItems: 'center'
                    }}
                  >
                    <X size={16} />
                  </button>
                </div>
              ))}
              
              {/* Select Countries Button */}
              <button
                type="button"
                onClick={openCountryModal}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '8px 16px',
                  background: 'rgba(59, 130, 246, 0.1)',
                  border: '2px dashed rgba(59, 130, 246, 0.4)',
                  borderRadius: 10,
                  color: '#3b82f6',
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
              >
                <Globe size={16} />
                Select Countries ({selectedCountries.length})
              </button>

              {/* Add Extra Country Search */}
              <div style={{ position: 'relative', minWidth: 200 }}>
                <input
                  type="text"
                  placeholder="Add extra country..."
                  value={extraCountrySearch}
                  onChange={(e) => {
                    setExtraCountrySearch(e.target.value);
                    setShowExtraCountryDropdown(e.target.value.length > 0);
                  }}
                  onFocus={() => setShowExtraCountryDropdown(extraCountrySearch.length > 0)}
                  onBlur={() => setTimeout(() => setShowExtraCountryDropdown(false), 200)}
                  style={{
                    padding: '8px 12px',
                    background: 'rgba(255, 255, 255, 0.05)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    borderRadius: 10,
                    color: 'white',
                    fontSize: 13,
                    width: '100%'
                  }}
                />
                {showExtraCountryDropdown && filteredExtraCountries.length > 0 && (
                  <div style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    marginTop: 4,
                    background: '#1a1a2e',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    borderRadius: 10,
                    maxHeight: 300,
                    overflow: 'auto',
                    zIndex: 1000,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.3)'
                  }}>
                    {filteredExtraCountries.map(country => (
                      <div
                        key={country.code}
                        onClick={() => addExtraCountry(country)}
                        onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.1)'}
                        onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                        style={{
                          padding: '10px 12px',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                          borderBottom: '1px solid rgba(255,255,255,0.05)',
                          color: 'white',
                          fontSize: 13
                        }}
                      >
                        <span style={{ fontSize: 18 }}>{country.flag}</span>
                        <span>{country.name}</span>
                        <span style={{ opacity: 0.5, fontSize: 11 }}>({country.geoCode})</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            
            {/* Manual City Input */}
            <div style={{ width: '100%', marginTop: 12 }}>
              <input
                type="text"
                placeholder="Add city manually (e.g., Valencia, Bali)..."
                value={cityInput}
                onChange={(e) => setCityInput(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && cityInput.trim()) {
                    const cityObject = getLocationObject(cityInput.trim());
                    if (!selectedCountries.some(c => c.name === cityObject.name)) {
                      setSelectedCountries([...selectedCountries, cityObject]);
                    }
                    setCityInput('');
                  }
                }}
                style={{
                  width: '100%',
                  padding: '10px 14px',
                  borderRadius: 8,
                  border: '2px solid rgba(139,92,246,0.3)',
                  background: 'rgba(0,0,0,0.3)',
                  color: 'white',
                  fontSize: 14,
                  outline: 'none'
                }}
              />
              <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: 12, marginTop: 6 }}>
                Type a city name and press Enter to add custom city targeting
              </div>
            </div>
            
            <div style={styles.hint}>
              💡 Select countries from the language list, or add any country manually
            </div>
          </div>
        )}
      </div>

      {/* Network Settings */}
      <div style={{ ...styles.card, marginTop: 20 }}>
        <h4 style={styles.cardTitle}>Network Settings</h4>
        <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: 14, marginBottom: 20 }}>
          Choose where your ads can appear
        </p>

        <div style={styles.checkboxGroup}>
          <label style={styles.checkboxLabel}>
            <input
              type="checkbox"
              checked={networkSettings.targetGoogleSearch}
              onChange={(e) => setNetworkSettings({
                ...networkSettings,
                targetGoogleSearch: e.target.checked
              })}
              style={styles.checkbox}
            />
            <div>
              <div style={{ fontWeight: 600, marginBottom: 4 }}>
                🔍 Google Search
              </div>
              <div style={{ fontSize: 13, opacity: 0.7 }}>
                Show ads on Google search results
              </div>
            </div>
          </label>

          <label style={styles.checkboxLabel}>
            <input
              type="checkbox"
              checked={networkSettings.targetSearchNetwork}
              onChange={(e) => setNetworkSettings({
                ...networkSettings,
                targetSearchNetwork: e.target.checked
              })}
              style={styles.checkbox}
            />
            <div>
              <div style={{ fontWeight: 600, marginBottom: 4 }}>
                🌐 Search Network
              </div>
              <div style={{ fontSize: 13, opacity: 0.7 }}>
                Show ads on Google search partners
              </div>
            </div>
          </label>

          <label style={styles.checkboxLabel}>
            <input
              type="checkbox"
              checked={networkSettings.targetContentNetwork}
              onChange={(e) => setNetworkSettings({
                ...networkSettings,
                targetContentNetwork: e.target.checked
              })}
              style={styles.checkbox}
            />
            <div>
              <div style={{ fontWeight: 600, marginBottom: 4 }}>
                📱 Display Expansion
              </div>
              <div style={{ fontSize: 13, opacity: 0.7 }}>
                Expand reach to Display Network (Search campaigns only)
              </div>
            </div>
          </label>

          <label style={styles.checkboxLabel}>
            <input
              type="checkbox"
              checked={networkSettings.targetPartnerSearchNetwork}
              onChange={(e) => setNetworkSettings({
                ...networkSettings,
                targetPartnerSearchNetwork: e.target.checked
              })}
              style={styles.checkbox}
            />
            <div>
              <div style={{ fontWeight: 600, marginBottom: 4 }}>
                🤝 Search Partners
              </div>
              <div style={{ fontSize: 13, opacity: 0.7 }}>
                Show ads on partner sites that partner with Google
              </div>
            </div>
          </label>
        </div>
      </div>

      {/* Schedule (Optional) */}
      <div style={{ ...styles.card, marginTop: 20 }}>
        <h4 style={styles.cardTitle}>
          <Calendar size={18} style={{ marginRight: 8 }} />
          Campaign Schedule (Optional)
        </h4>
        
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 15 }}>
          <div style={styles.formGroup}>
            <label style={styles.label}>Start Date</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              min={new Date().toISOString().split('T')[0]}
              style={styles.input}
            />
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>End Date</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              min={startDate || new Date().toISOString().split('T')[0]}
              style={styles.input}
            />
          </div>
        </div>
        <div style={styles.hint}>
          Leave empty for continuous campaign
        </div>
      </div>

      {/* Advanced Settings */}
      <div style={{ ...styles.card, marginTop: 20 }}>
        <button
          onClick={() => setShowAdvanced(!showAdvanced)}
          style={styles.expandButton}
        >
          <span>Advanced Settings</span>
          {showAdvanced ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
        </button>

        {showAdvanced && (
          <div style={{ marginTop: 15 }}>
            <label style={styles.checkboxLabel}>
              <input
                type="checkbox"
                checked={containsEuPolitical}
                onChange={(e) => setContainsEuPolitical(e.target.checked)}
                style={styles.checkbox}
              />
              <div>
                <div style={{ fontWeight: 600, marginBottom: 4 }}>
                  Contains EU Political Advertising
                </div>
                <div style={{ fontSize: 13, opacity: 0.7 }}>
                  Check if this campaign contains political advertising targeting EU
                </div>
              </div>
            </label>
          </div>
        )}
      </div>
    </div>
  );

  const renderKeywordsAds = () => (
    <div style={styles.stepContent}>
      <h3 style={styles.stepTitle}>
        <Zap size={24} style={{ color: '#f59e0b', marginRight: 12 }} />
        Keywords & Ads
      </h3>
      <p style={styles.stepDescription}>
        Add keywords and create your ad content
      </p>

      {isMultipleAdGroupMode && (
        <>
          <div style={{
            padding: 16,
            background: 'rgba(59, 130, 246, 0.1)',
            border: '1px solid rgba(59, 130, 246, 0.3)',
            borderRadius: 12,
            marginBottom: 20,
            color: 'rgba(255,255,255,0.75)',
            fontSize: 13,
            lineHeight: 1.6
          }}>
            Multiple ad groups are created only when you submit the campaign. No extra AI or Google Ads calls run while editing.
          </div>

          {multiAdGroups.map((group, groupIndex) => {
            const validGroupHeadlines = group.headlines.filter(h => h.trim());
            const validGroupDescriptions = group.descriptions.filter(d => d.trim());

            return (
              <div key={group.id} style={{ ...styles.card, marginBottom: 20 }}>
                <h4 style={styles.cardTitle}>
                  {group.name || `${campaignName || 'Campaign'} - Ad Group ${groupIndex + 1}`}
                  {(group.keywords.length === 0 || validGroupHeadlines.length < 3 || validGroupDescriptions.length < 2) && (
                    <span style={{ color: '#ef4444', fontSize: 14, marginLeft: 8 }}>
                      *Keywords, 3 headlines and 2 descriptions required
                    </span>
                  )}
                </h4>

                <div style={styles.formGroup}>
                  <label style={styles.label}>Ad Group Name</label>
                  <input
                    type="text"
                    value={group.name}
                    onChange={(e) => updateMultiAdGroup(groupIndex, { name: e.target.value })}
                    placeholder={`${campaignName || 'Campaign'} - Ad Group ${groupIndex + 1}`}
                    style={styles.input}
                  />
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>Keywords ({group.keywords.length})</label>
                  <div style={styles.keywordInputRow}>
                    <input
                      type="text"
                      value={group.keywordInput}
                      onChange={(e) => updateMultiAdGroup(groupIndex, { keywordInput: e.target.value })}
                      onKeyPress={(e) => e.key === 'Enter' && addMultiAdGroupKeyword(groupIndex)}
                      placeholder="Enter a keyword..."
                      style={{ ...styles.input, flex: 1, marginBottom: 0 }}
                    />
                    <select
                      value={group.matchType}
                      onChange={(e) => updateMultiAdGroup(groupIndex, { matchType: e.target.value })}
                      style={{ ...styles.select, width: 140, marginBottom: 0 }}
                    >
                      <option value="BROAD">Broad</option>
                      <option value="PHRASE">Phrase</option>
                      <option value="EXACT">Exact</option>
                    </select>
                    <button onClick={() => addMultiAdGroupKeyword(groupIndex)} style={styles.addButton}>
                      <Plus size={18} />
                    </button>
                  </div>

                  {group.keywords.length > 0 && (
                    <div style={styles.keywordList}>
                      {group.keywords.map((kw, keywordIndex) => (
                        <div key={`${kw.text}-${keywordIndex}`} style={styles.keywordChip}>
                          <span style={{ flex: 1 }}>
                            {kw.matchType === 'EXACT' && '"'}
                            {kw.matchType === 'PHRASE' && '"'}
                            {kw.text}
                            {kw.matchType === 'EXACT' && '"'}
                            {kw.matchType === 'PHRASE' && '"'}
                          </span>
                          <span style={styles.keywordMatchType}>{kw.matchType}</span>
                          <button
                            onClick={() => removeMultiAdGroupKeyword(groupIndex, keywordIndex)}
                            style={styles.removeButton}
                          >
                            <X size={14} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>Final URL</label>
                  <input
                    type="url"
                    value={group.finalUrl}
                    onChange={(e) => updateMultiAdGroup(groupIndex, { finalUrl: e.target.value })}
                    placeholder="https://example.com/landing-page"
                    style={styles.input}
                  />
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 15 }}>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Path 1 (Optional)</label>
                    <input
                      type="text"
                      value={group.path1}
                      onChange={(e) => updateMultiAdGroup(groupIndex, { path1: e.target.value.substring(0, 15) })}
                      placeholder="e.g., products"
                      maxLength={15}
                      style={styles.input}
                    />
                  </div>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Path 2 (Optional)</label>
                    <input
                      type="text"
                      value={group.path2}
                      onChange={(e) => updateMultiAdGroup(groupIndex, { path2: e.target.value.substring(0, 15) })}
                      placeholder="e.g., best-deals"
                      maxLength={15}
                      style={styles.input}
                    />
                  </div>
                </div>

                <div style={styles.formGroup}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                    <label style={styles.label}>Headlines ({validGroupHeadlines.length}/15)</label>
                    {group.headlines.length < 15 && (
                      <button onClick={() => addMultiAdGroupHeadline(groupIndex)} style={styles.smallAddButton}>
                        <Plus size={14} /> Add
                      </button>
                    )}
                  </div>
                  {group.headlines.map((headline, headlineIndex) => (
                    <div key={headlineIndex} style={styles.adInputRow}>
                      <input
                        type="text"
                        value={headline}
                        onChange={(e) => updateMultiAdGroupHeadline(groupIndex, headlineIndex, e.target.value)}
                        placeholder={`Headline ${headlineIndex + 1} (max 30 characters)`}
                        style={styles.input}
                      />
                      <span style={styles.charCount}>{headline.length}/30</span>
                    </div>
                  ))}
                </div>

                <div style={styles.formGroup}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                    <label style={styles.label}>Descriptions ({validGroupDescriptions.length}/4)</label>
                    {group.descriptions.length < 4 && (
                      <button onClick={() => addMultiAdGroupDescription(groupIndex)} style={styles.smallAddButton}>
                        <Plus size={14} /> Add
                      </button>
                    )}
                  </div>
                  {group.descriptions.map((description, descriptionIndex) => (
                    <div key={descriptionIndex} style={styles.adInputRow}>
                      <textarea
                        value={description}
                        onChange={(e) => updateMultiAdGroupDescription(groupIndex, descriptionIndex, e.target.value)}
                        placeholder={`Description ${descriptionIndex + 1} (max 90 characters)`}
                        rows={2}
                        style={{ ...styles.input, resize: 'vertical' }}
                      />
                      <span style={styles.charCount}>{description.length}/90</span>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}

          {/* Extensions are campaign-level, so they still apply once to the campaign. */}
        </>
      )}

      {!isMultipleAdGroupMode && (
        <>

      {/* Keywords */}
      <div style={styles.card}>
        <h4 style={styles.cardTitle}>
          Keywords ({keywords.length})
          {keywords.length === 0 && <span style={{ color: '#ef4444', fontSize: 14, marginLeft: 8 }}>*Required</span>}
        </h4>
        
        <div style={styles.keywordInputRow}>
          <input
            type="text"
            value={keywordInput}
            onChange={(e) => setKeywordInput(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && addKeyword()}
            placeholder="Enter a keyword..."
            style={{ ...styles.input, flex: 1, marginBottom: 0 }}
          />
          <select
            value={matchType}
            onChange={(e) => setMatchType(e.target.value)}
            style={{ ...styles.select, width: 140, marginBottom: 0 }}
          >
            <option value="BROAD">Broad</option>
            <option value="PHRASE">Phrase</option>
            <option value="EXACT">Exact</option>
          </select>
          <button onClick={addKeyword} style={styles.addButton}>
            <Plus size={18} />
          </button>
        </div>

        {keywords.length > 0 && (
          <div style={styles.keywordList}>
            {keywords.map((kw, index) => (
              <div key={index} style={styles.keywordChip}>
                <span style={{ flex: 1 }}>
                  {kw.matchType === 'EXACT' && '"'}
                  {kw.matchType === 'PHRASE' && '"'}
                  {kw.text}
                  {kw.matchType === 'EXACT' && '"'}
                  {kw.matchType === 'PHRASE' && '"'}
                </span>
                <span style={styles.keywordMatchType}>{kw.matchType}</span>
                <button 
                  onClick={() => removeKeyword(index)}
                  style={styles.removeButton}
                >
                  <X size={14} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Ad Content */}
      <div style={{ ...styles.card, marginTop: 20 }}>
        <h4 style={styles.cardTitle}>
          Responsive Search Ad
          {(headlines.filter(h => h.trim()).length < 3 || descriptions.filter(d => d.trim()).length < 2) && (
            <span style={{ color: '#ef4444', fontSize: 14, marginLeft: 8 }}>
              *Required: Min 3 headlines & 2 descriptions
            </span>
          )}
        </h4>

        <div style={styles.formGroup}>
          <label style={styles.label}>
            Final URL <span style={{ color: '#ef4444' }}>*</span>
          </label>
          <input
            type="url"
            value={finalUrl}
            onChange={(e) => setFinalUrl(e.target.value)}
            placeholder="https://example.com/landing-page"
            style={styles.input}
          />
        </div>

        {/* Path Fields */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 15 }}>
          <div style={styles.formGroup}>
            <label style={styles.label}>Path 1 (Optional)</label>
            <input
              type="text"
              value={path1}
              onChange={(e) => setPath1(e.target.value.substring(0, 15))}
              placeholder="e.g., products"
              maxLength={15}
              style={styles.input}
            />
            <div style={styles.hint}>{path1.length}/15 characters</div>
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>Path 2 (Optional)</label>
            <input
              type="text"
              value={path2}
              onChange={(e) => setPath2(e.target.value.substring(0, 15))}
              placeholder="e.g., best-deals"
              maxLength={15}
              style={styles.input}
            />
            <div style={styles.hint}>{path2.length}/15 characters</div>
          </div>
        </div>

        {/* URL Preview */}
        {(path1 || path2) && (
          <div style={{
            padding: 12,
            background: 'rgba(59, 130, 246, 0.1)',
            border: '1px solid rgba(59, 130, 246, 0.3)',
            borderRadius: 8,
            marginBottom: 20
          }}>
            <div style={{ fontSize: 12, color: 'rgba(255, 255, 255, 0.6)', marginBottom: 4 }}>
              URL Preview:
            </div>
            <div style={{ fontSize: 14, color: '#60a5fa', fontWeight: 500 }}>
              {finalUrl.replace(/\/+$/, '')}{path1 ? `/${path1}` : ''}{path2 ? `/${path2}` : ''}
            </div>
          </div>
        )}

        {/* AI Generation Button */}
        <div style={{
          background: 'rgba(139, 92, 246, 0.1)',
          border: '1px solid rgba(139, 92, 246, 0.3)',
          borderRadius: 12,
          padding: 20,
          marginBottom: 20
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <h5 style={{ color: 'white', fontSize: 16, fontWeight: 600, marginBottom: 6, display: 'flex', alignItems: 'center', gap: 8 }}>
                <Sparkles size={20} style={{ color: '#a78bfa' }} />
                AI Ad Generation
              </h5>
              <p style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: 13, margin: 0 }}>
                Generate headlines and descriptions automatically with AI
              </p>
            </div>
            <button
              onClick={() => setShowAiModal(true)}
              style={{
                padding: '10px 20px',
                background: 'linear-gradient(135deg, #8b5cf6, #a78bfa)',
                border: 'none',
                borderRadius: 8,
                color: 'white',
                cursor: 'pointer',
                fontSize: 14,
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                gap: 8
              }}
            >
              <Sparkles size={16} />
              Generate with AI
            </button>
          </div>
        </div>

        <div style={styles.formGroup}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
            <label style={styles.label}>
              Headlines ({headlines.filter(h => h.trim()).length}/15)
            </label>
            {headlines.length < 15 && (
              <button onClick={addHeadline} style={styles.smallAddButton}>
                <Plus size={14} /> Add
              </button>
            )}
          </div>
          {headlines.map((headline, index) => (
            <div key={index} style={styles.adInputRow}>
              <input
                type="text"
                value={headline}
                onChange={(e) => updateHeadline(index, e.target.value)}
                placeholder={`Headline ${index + 1} (max 30 characters)`}
                style={styles.input}
              />
              <span style={styles.charCount}>{headline.length}/30</span>
              {headlines.length > 3 && (
                <button onClick={() => removeHeadline(index)} style={styles.removeButton}>
                  <Trash2 size={14} />
                </button>
              )}
            </div>
          ))}
        </div>

        <div style={styles.formGroup}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
            <label style={styles.label}>
              Descriptions ({descriptions.filter(d => d.trim()).length}/4)
            </label>
            {descriptions.length < 4 && (
              <button onClick={addDescription} style={styles.smallAddButton}>
                <Plus size={14} /> Add
              </button>
            )}
          </div>
          {descriptions.map((desc, index) => (
            <div key={index} style={styles.adInputRow}>
              <textarea
                value={desc}
                onChange={(e) => updateDescription(index, e.target.value)}
                placeholder={`Description ${index + 1} (max 90 characters)`}
                rows={2}
                style={{ ...styles.input, resize: 'vertical' }}
              />
              <span style={styles.charCount}>{desc.length}/90</span>
              {descriptions.length > 2 && (
                <button onClick={() => removeDescription(index)} style={styles.removeButton}>
                  <Trash2 size={14} />
                </button>
              )}
            </div>
          ))}
        </div>
      </div>

        </>
      )}

      {/* Extensions Section */}
      <div style={{ ...styles.card, marginTop: 20 }}>
        <h4 style={styles.cardTitle}>Ad Extensions (Optional)</h4>
        <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: 14, marginBottom: 20 }}>
          Add extra information to make your ads more compelling
        </p>

        {/* Extension Mode Toggle */}
        <div style={styles.buttonGroup}>
          {[
            { value: 'none', label: '❌ No Extensions', desc: 'Skip for now' },
            { value: 'ai', label: '✨ Generate with AI', desc: 'Auto-generate' },
            { value: 'manual', label: '✏️ Enter Manually', desc: 'Custom input' }
          ].map(option => (
            <button
              key={option.value}
              onClick={() => setExtensionsMode(option.value)}
              style={{
                ...styles.optionButton,
                ...(extensionsMode === option.value ? styles.optionButtonActive : {})
              }}
            >
              <div style={{ fontWeight: 600, marginBottom: 4 }}>{option.label}</div>
              <div style={{ fontSize: 11, opacity: 0.7 }}>{option.desc}</div>
            </button>
          ))}
        </div>

        {extensionsMode === 'ai' && (
          <div style={{
            marginTop: 20,
            padding: 20,
            background: 'rgba(139, 92, 246, 0.1)',
            border: '1px solid rgba(139, 92, 246, 0.3)',
            borderRadius: 12
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ color: 'white', fontSize: 16, fontWeight: 600, marginBottom: 6, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Sparkles size={20} style={{ color: '#a78bfa' }} />
                  Generate Extensions with AI
                </div>
                <p style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: 13, margin: 0 }}>
                  Generate sitelinks, callouts, and structured snippets based on your keywords
                </p>
              </div>
              <button
                onClick={async () => {
                  setAiGenerating(true);
                  try {
                    const firstMultiKeyword = multiAdGroups.find(group => group.keywords.length > 0)?.keywords[0]?.text;
                    const keywordForAds = (isMultipleAdGroupMode ? firstMultiKeyword : keywords[0]?.text) || campaignName || 'product';
                    console.log('🚀 Starting AI extension generation...');
                    console.log('  Keyword:', keywordForAds);
                    console.log('  Language:', language);
                    
                    // Get auth token
                    const token = await getToken();
                    
                    const response = await fetch(`${API_URL}/ads/generate-multilang`, {
                      method: 'POST',
                      headers: { 
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                      },
                      body: JSON.stringify({
                        keyword: keywordForAds,
                        languages: [language],
                        businessInfo,
                        landingPageUrl: landingPageUrl || businessInfo.website || finalUrl,
                        analyzeCompetitors,
                        descriptionTypes
                      })
                    });
                    
                    console.log('✅ API Response received:', response.status);
                    
                    if (!response.ok) {
                      throw new Error(`API returned status ${response.status}`);
                    }
                    
                    const data = await response.json();
                    console.log('📦 Response data:', data);
                    
                    if (data.success && data.data && data.data[language]) {
                      const adData = data.data[language];
                      console.log('✨ Extension data:', {
                        sitelinks: adData.sitelinks?.length || 0,
                        callouts: adData.callouts?.length || 0,
                        structuredSnippets: adData.structuredSnippets?.length || 0
                      });
                      
                      // Extract extensions
                      setSitelinks(adData.sitelinks || []);
                      setCallouts(adData.callouts || []);
                      setStructuredSnippets(adData.structuredSnippets || []);
                      
                      const totalGenerated = (adData.sitelinks || []).length + 
                                            (adData.callouts || []).length + 
                                            (adData.structuredSnippets || []).length;
                      
                      if (totalGenerated > 0) {
                        alert(`✨ Successfully generated:\n• ${(adData.sitelinks || []).length} sitelinks\n• ${(adData.callouts || []).length} callouts\n• ${(adData.structuredSnippets || []).length} structured snippets`);
                      } else {
                        alert('⚠️ Extensions were generated but none were returned. Please try again.');
                      }
                    } else {
                      console.error('❌ Invalid response structure:', data);
                      throw new Error(data.error || 'Invalid response from API');
                    }
                  } catch (error) {
                    console.error('❌ Error generating extensions:', error);
                    alert(`Failed to generate extensions:\n${error.message}\n\nPlease check the console for more details.`);
                  } finally {
                    setAiGenerating(false);
                  }
                }}
                disabled={aiGenerating}
                style={{
                  padding: '10px 20px',
                  background: aiGenerating ? 'rgba(139, 92, 246, 0.5)' : 'linear-gradient(135deg, #8b5cf6, #a78bfa)',
                  border: 'none',
                  borderRadius: 8,
                  color: 'white',
                  cursor: aiGenerating ? 'not-allowed' : 'pointer',
                  fontSize: 14,
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8
                }}
              >
                {aiGenerating ? (
                  <>
                    <RefreshCw size={16} style={{ animation: 'spin 1s linear infinite' }} />
                    Generating...
                  </>
                ) : (
                  <>
                    <Sparkles size={16} />
                    Generate
                  </>
                )}
              </button>
            </div>

            {/* Display Generated Extensions */}
            {(sitelinks.length > 0 || callouts.length > 0 || structuredSnippets.length > 0) && (
              <div style={{ marginTop: 20, display: 'flex', flexDirection: 'column', gap: 16 }}>
                {sitelinks.length > 0 && (
                  <div>
                    <div style={{ color: 'white', fontSize: 14, fontWeight: 600, marginBottom: 8 }}>
                      Sitelinks ({sitelinks.length})
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                      {sitelinks.map((link, i) => (
                        <div key={i} style={{
                          padding: '6px 12px',
                          background: 'rgba(139, 92, 246, 0.2)',
                          border: '1px solid rgba(139, 92, 246, 0.4)',
                          borderRadius: 6,
                          fontSize: 13,
                          color: 'white'
                        }}>
                          {link.text}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {callouts.length > 0 && (
                  <div>
                    <div style={{ color: 'white', fontSize: 14, fontWeight: 600, marginBottom: 8 }}>
                      Callouts ({callouts.length})
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                      {callouts.map((callout, i) => (
                        <div key={i} style={{
                          padding: '6px 12px',
                          background: 'rgba(59, 130, 246, 0.2)',
                          border: '1px solid rgba(59, 130, 246, 0.4)',
                          borderRadius: 6,
                          fontSize: 13,
                          color: 'white'
                        }}>
                          {callout.text}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {structuredSnippets.length > 0 && (
                  <div>
                    <div style={{ color: 'white', fontSize: 14, fontWeight: 600, marginBottom: 8 }}>
                      Structured Snippets ({structuredSnippets.length})
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                      {structuredSnippets.map((snippet, i) => (
                        <div key={i} style={{
                          padding: '6px 12px',
                          background: 'rgba(16, 185, 129, 0.2)',
                          border: '1px solid rgba(16, 185, 129, 0.4)',
                          borderRadius: 6,
                          fontSize: 13,
                          color: 'white'
                        }}>
                          {snippet.header}: {snippet.values.join(', ')}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {extensionsMode === 'manual' && (
          <div style={{ marginTop: 20 }}>
            <div style={{
              padding: 16,
              background: 'rgba(59, 130, 246, 0.1)',
              border: '1px solid rgba(59, 130, 246, 0.3)',
              borderRadius: 8,
              fontSize: 13,
              color: 'rgba(255, 255, 255, 0.7)',
              textAlign: 'center'
            }}>
              <Info size={16} style={{ display: 'inline', marginRight: 6, verticalAlign: 'middle' }} />
              Manual extension entry coming soon. For now, use AI generation or add extensions after campaign creation in Google Ads.
            </div>
          </div>
        )}
      </div>
    </div>
  );

  // Performance Max Assets (Step 4 for Pmax campaigns)
  const renderPerformanceMaxAssets = () => (
    <div style={styles.stepContent}>
      <h3 style={styles.stepTitle}>
        <Sparkles size={24} style={{ color: '#8b5cf6', marginRight: 12 }} />
        Performance Max Assets
      </h3>
      <p style={styles.stepDescription}>
        Provide assets for your AI-powered Performance Max campaign
      </p>

      {/* Final URL */}
      <div style={styles.formGroup}>
        <label style={styles.label}>
          Final URL (Landing Page) <span style={{ color: '#ef4444' }}>*</span>
        </label>
        <input
          type="url"
          value={finalUrl}
          onChange={(e) => setFinalUrl(e.target.value)}
          placeholder="https://example.com"
          style={styles.input}
        />
        <div style={styles.hint}>
          Where users will land after clicking your ad
        </div>
      </div>

      {/* Business Name */}
      <div style={styles.formGroup}>
        <label style={styles.label}>Business Name (Optional)</label>
        <input
          type="text"
          value={businessName}
          onChange={(e) => setBusinessName(e.target.value)}
          placeholder="Your Business Name"
          style={styles.input}
          maxLength={25}
        />
      </div>

      {/* Headlines */}
      <div style={styles.card}>
        <h4 style={styles.cardTitle}>Headlines (3-5 required)</h4>
        {headlines.map((headline, index) => (
          <div key={index} style={styles.formGroup}>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
              <input
                type="text"
                value={headline}
                onChange={(e) => {
                  const newHeadlines = [...headlines];
                  newHeadlines[index] = e.target.value;
                  setHeadlines(newHeadlines);
                }}
                placeholder={`Headline ${index + 1}`}
                style={styles.input}
              />
              <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: 12, minWidth: 40 }}>
                {headline.length}/30
              </span>
            </div>
          </div>
        ))}
      </div>

      {/* Descriptions */}
      <div style={styles.card}>
        <h4 style={styles.cardTitle}>Descriptions (2-4 required)</h4>
        {descriptions.map((description, index) => (
          <div key={index} style={styles.formGroup}>
            <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
              <textarea
                value={description}
                onChange={(e) => {
                  const newDescriptions = [...descriptions];
                  newDescriptions[index] = e.target.value;
                  setDescriptions(newDescriptions);
                }}
                placeholder={`Description ${index + 1}`}
                rows={2}
                style={styles.textarea}
              />
              <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: 12, minWidth: 40 }}>
                {description.length}/90
              </span>
            </div>
          </div>
        ))}
      </div>

      {/* Images */}
      <div style={styles.card}>
        <h4 style={styles.cardTitle}>Images (URLs)</h4>
        <p style={styles.hint}>📸 Landscape (1200x628) and Square (1200x1200) required</p>
        
        <div style={styles.formGroup}>
          <label style={styles.label}>Landscape Images (1200x628)</label>
          {imageUrls.landscape.map((url, index) => (
            <div key={index} style={{ display: 'flex', gap: 10, marginBottom: 10 }}>
              <input
                type="url"
                value={url}
                onChange={(e) => {
                  const newUrls = [...imageUrls.landscape];
                  newUrls[index] = e.target.value;
                  setImageUrls({...imageUrls, landscape: newUrls});
                }}
                placeholder="https://example.com/image.jpg"
                style={styles.input}
              />
              <button
                onClick={() => {
                  setImageUrls({
                    ...imageUrls, 
                    landscape: imageUrls.landscape.filter((_, i) => i !== index)
                  });
                }}
                style={{...styles.iconButton, background: '#ef4444'}}
              >
                <Trash2 size={16} />
              </button>
            </div>
          ))}
          <button
            onClick={() => setImageUrls({...imageUrls, landscape: [...imageUrls.landscape, '']})}
            style={{...styles.button, background: 'rgba(139,92,246,0.2)', color: '#a78bfa'}}
          >
            <Plus size={16} /> Add Landscape Image
          </button>
        </div>

        <div style={styles.formGroup}>
          <label style={styles.label}>Square Images (1200x1200)</label>
          {imageUrls.square.map((url, index) => (
            <div key={index} style={{ display: 'flex', gap: 10, marginBottom: 10 }}>
              <input
                type="url"
                value={url}
                onChange={(e) => {
                  const newUrls = [...imageUrls.square];
                  newUrls[index] = e.target.value;
                  setImageUrls({...imageUrls, square: newUrls});
                }}
                placeholder="https://example.com/square-image.jpg"
                style={styles.input}
              />
              <button
                onClick={() => {
                  setImageUrls({
                    ...imageUrls, 
                    square: imageUrls.square.filter((_, i) => i !== index)
                  });
                }}
                style={{...styles.iconButton, background: '#ef4444'}}
              >
                <Trash2 size={16} />
              </button>
            </div>
          ))}
          <button
            onClick={() => setImageUrls({...imageUrls, square: [...imageUrls.square, '']})}
            style={{...styles.button, background: 'rgba(139,92,246,0.2)', color: '#a78bfa'}}
          >
            <Plus size={16} /> Add Square Image
          </button>
        </div>
      </div>

      <div style={{
        marginTop: 20,
        padding: 16,
        background: 'rgba(139,92,246,0.1)',
        borderRadius: 12,
        border: '1px solid rgba(139,92,246,0.3)'
      }}>
        <p style={{ color: '#a78bfa', fontSize: 14, margin: 0 }}>
          💡 <strong>Performance Max Tips:</strong> Google's AI will automatically optimize which assets perform best across Search, Display, YouTube, Gmail, and Maps.
        </p>
      </div>
    </div>
  );

  // Demand Gen Assets (Step 4 for Demand Gen campaigns)
  const renderDemandGenAssets = () => (
    <div style={styles.stepContent}>
      <h3 style={styles.stepTitle}>
        <Globe size={24} style={{ color: '#3b82f6', marginRight: 12 }} />
        Demand Gen Creative Assets
      </h3>
      <p style={styles.stepDescription}>
        Create visual ads for YouTube, Gmail, and Discover
      </p>

      {/* Headlines */}
      <div style={styles.card}>
        <h4 style={styles.cardTitle}>Headlines (2-5 recommended)</h4>
        {headlines.slice(0, 5).map((headline, index) => (
          <div key={index} style={styles.formGroup}>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
              <input
                type="text"
                value={headline}
                onChange={(e) => {
                  const newHeadlines = [...headlines];
                  newHeadlines[index] = e.target.value;
                  setHeadlines(newHeadlines);
                }}
                placeholder={`Headline ${index + 1}`}
                style={styles.input}
              />
              <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: 12, minWidth: 40 }}>
                {headline.length}/40
              </span>
            </div>
          </div>
        ))}
      </div>

      {/* Descriptions */}
      <div style={styles.card}>
        <h4 style={styles.cardTitle}>Descriptions (2-5 recommended)</h4>
        {descriptions.slice(0, 5).map((description, index) => (
          <div key={index} style={styles.formGroup}>
            <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
              <textarea
                value={description}
                onChange={(e) => {
                  const newDescriptions = [...descriptions];
                  newDescriptions[index] = e.target.value;
                  setDescriptions(newDescriptions);
                }}
                placeholder={`Description ${index + 1}`}
                rows={2}
                style={styles.textarea}
              />
              <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: 12, minWidth: 40 }}>
                {description.length}/90
              </span>
            </div>
          </div>
        ))}
      </div>

      {/* Images */}
      <div style={styles.card}>
        <h4 style={styles.cardTitle}>Images (URLs) <span style={{ color: '#ef4444' }}>*</span></h4>
        <p style={styles.hint}>At least one landscape (1200x628) OR square (1200x1200) image required</p>
        
        <div style={styles.formGroup}>
          <label style={styles.label}>Landscape Images (1200x628)</label>
          {imageUrls.landscape.map((url, index) => (
            <div key={index} style={{ display: 'flex', gap: 10, marginBottom: 10 }}>
              <input
                type="url"
                value={url}
                onChange={(e) => {
                  const newUrls = [...imageUrls.landscape];
                  newUrls[index] = e.target.value;
                  setImageUrls({...imageUrls, landscape: newUrls});
                }}
                placeholder="https://example.com/image.jpg"
                style={styles.input}
              />
              <button
                onClick={() => {
                  setImageUrls({
                    ...imageUrls, 
                    landscape: imageUrls.landscape.filter((_, i) => i !== index)
                  });
                }}
                style={{...styles.iconButton, background: '#ef4444'}}
              >
                <Trash2 size={16} />
              </button>
            </div>
          ))}
          <button
            onClick={() => setImageUrls({...imageUrls, landscape: [...imageUrls.landscape, '']})}
            style={{...styles.button, background: 'rgba(59,130,246,0.2)', color: '#60a5fa'}}
          >
            <Plus size={16} /> Add Landscape Image
          </button>
        </div>

        <div style={styles.formGroup}>
          <label style={styles.label}>Square Images (1200x1200)</label>
          {imageUrls.square.map((url, index) => (
            <div key={index} style={{ display: 'flex', gap: 10, marginBottom: 10 }}>
              <input
                type="url"
                value={url}
                onChange={(e) => {
                  const newUrls = [...imageUrls.square];
                  newUrls[index] = e.target.value;
                  setImageUrls({...imageUrls, square: newUrls});
                }}
                placeholder="https://example.com/square-image.jpg"
                style={styles.input}
              />
              <button
                onClick={() => {
                  setImageUrls({
                    ...imageUrls, 
                    square: imageUrls.square.filter((_, i) => i !== index)
                  });
                }}
                style={{...styles.iconButton, background: '#ef4444'}}
              >
                <Trash2 size={16} />
              </button>
            </div>
          ))}
          <button
            onClick={() => setImageUrls({...imageUrls, square: [...imageUrls.square, '']})}
            style={{...styles.button, background: 'rgba(59,130,246,0.2)', color: '#60a5fa'}}
          >
            <Plus size={16} /> Add Square Image
          </button>
        </div>
      </div>

      {/* Call to Action */}
      <div style={styles.formGroup}>
        <label style={styles.label}>Call to Action</label>
        <select
          value={callToAction}
          onChange={(e) => setCallToAction(e.target.value)}
          style={styles.select}
        >
          <option value="LEARN_MORE">Learn More</option>
          <option value="GET_QUOTE">Get Quote</option>
          <option value="APPLY_NOW">Apply Now</option>
          <option value="SIGN_UP">Sign Up</option>
          <option value="CONTACT_US">Contact Us</option>
          <option value="SUBSCRIBE">Subscribe</option>
          <option value="DOWNLOAD">Download</option>
          <option value="BOOK_NOW">Book Now</option>
          <option value="SHOP_NOW">Shop Now</option>
        </select>
      </div>
    </div>
  );

  // Display Assets (Step 4 for Display campaigns)
  const renderDisplayAssets = () => (
    <div style={styles.stepContent}>
      <h3 style={styles.stepTitle}>
        <Globe size={24} style={{ color: '#10b981', marginRight: 12 }} />
        Display Ad Assets
      </h3>
      <p style={styles.stepDescription}>
        Create responsive display ads for the Google Display Network
      </p>

      {/* Headlines */}
      <div style={styles.card}>
        <h4 style={styles.cardTitle}>Short Headlines (3-5 required, 30 chars each)</h4>
        {headlines.slice(0, 5).map((headline, index) => (
          <div key={index} style={styles.formGroup}>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
              <input
                type="text"
                value={headline}
                onChange={(e) => {
                  const newHeadlines = [...headlines];
                  newHeadlines[index] = e.target.value;
                  setHeadlines(newHeadlines);
                }}
                placeholder={`Short Headline ${index + 1}`}
                style={styles.input}
              />
              <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: 12, minWidth: 40 }}>
                {headline.length}/30
              </span>
            </div>
          </div>
        ))}
      </div>

      {/* Long Headline */}
      <div style={styles.formGroup}>
        <label style={styles.label}>Long Headline (90 chars, optional)</label>
        <input
          type="text"
          value={longHeadline}
          onChange={(e) => setLongHeadline(e.target.value)}
          placeholder="A longer headline for larger ad sizes"
          style={styles.input}
        />
        <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: 12 }}>
          {longHeadline.length}/90
        </span>
      </div>

      {/* Descriptions */}
      <div style={styles.card}>
        <h4 style={styles.cardTitle}>Descriptions (2-5 required, 90 chars each)</h4>
        {descriptions.slice(0, 5).map((description, index) => (
          <div key={index} style={styles.formGroup}>
            <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
              <textarea
                value={description}
                onChange={(e) => {
                  const newDescriptions = [...descriptions];
                  newDescriptions[index] = e.target.value;
                  setDescriptions(newDescriptions);
                }}
                placeholder={`Description ${index + 1}`}
                rows={2}
                style={styles.textarea}
              />
              <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: 12, minWidth: 40 }}>
                {description.length}/90
              </span>
            </div>
          </div>
        ))}
      </div>

      {/* Business Name */}
      <div style={styles.formGroup}>
        <label style={styles.label}>Business Name (Optional, 25 chars)</label>
        <input
          type="text"
          value={businessName}
          onChange={(e) => setBusinessName(e.target.value)}
          placeholder="Your Business Name"
          style={styles.input}
          maxLength={25}
        />
      </div>

      {/* Images */}
      <div style={styles.card}>
        <h4 style={styles.cardTitle}>Images (URLs) <span style={{ color: '#ef4444' }}>*</span></h4>
        <p style={styles.hint}>At least one landscape (1200x628) OR square (1200x1200) required</p>
        
        <div style={styles.formGroup}>
          <label style={styles.label}>Marketing Images - Landscape (1200x628)</label>
          {imageUrls.landscape.map((url, index) => (
            <div key={index} style={{ display: 'flex', gap: 10, marginBottom: 10 }}>
              <input
                type="url"
                value={url}
                onChange={(e) => {
                  const newUrls = [...imageUrls.landscape];
                  newUrls[index] = e.target.value;
                  setImageUrls({...imageUrls, landscape: newUrls});
                }}
                placeholder="https://example.com/image.jpg"
                style={styles.input}
              />
              <button
                onClick={() => {
                  setImageUrls({
                    ...imageUrls, 
                    landscape: imageUrls.landscape.filter((_, i) => i !== index)
                  });
                }}
                style={{...styles.iconButton, background: '#ef4444'}}
              >
                <Trash2 size={16} />
              </button>
            </div>
          ))}
          <button
            onClick={() => setImageUrls({...imageUrls, landscape: [...imageUrls.landscape, '']})}
            style={{...styles.button, background: 'rgba(16,185,129,0.2)', color: '#34d399'}}
          >
            <Plus size={16} /> Add Landscape Image
          </button>
        </div>

        <div style={styles.formGroup}>
          <label style={styles.label}>Marketing Images - Square (1200x1200)</label>
          {imageUrls.square.map((url, index) => (
            <div key={index} style={{ display: 'flex', gap: 10, marginBottom: 10 }}>
              <input
                type="url"
                value={url}
                onChange={(e) => {
                  const newUrls = [...imageUrls.square];
                  newUrls[index] = e.target.value;
                  setImageUrls({...imageUrls, square: newUrls});
                }}
                placeholder="https://example.com/square-image.jpg"
                style={styles.input}
              />
              <button
                onClick={() => {
                  setImageUrls({
                    ...imageUrls, 
                    square: imageUrls.square.filter((_, i) => i !== index)
                  });
                }}
                style={{...styles.iconButton, background: '#ef4444'}}
              >
                <Trash2 size={16} />
              </button>
            </div>
          ))}
          <button
            onClick={() => setImageUrls({...imageUrls, square: [...imageUrls.square, '']})}
            style={{...styles.button, background: 'rgba(16,185,129,0.2)', color: '#34d399'}}
          >
            <Plus size={16} /> Add Square Image
          </button>
        </div>
      </div>
    </div>
  );

  const renderReview = () => {
    const selectedLang = availableLanguages.find(l => l.code === language);
    const validHeadlines = headlines.filter(h => h.trim());
    const validDescriptions = descriptions.filter(d => d.trim());
    const reviewAdGroups = isMultipleAdGroupMode ? multiAdGroups : [];
    const reviewKeywordCount = isMultipleAdGroupMode
      ? reviewAdGroups.reduce((sum, group) => sum + group.keywords.length, 0)
      : keywords.length;
    const reviewAdCount = isMultipleAdGroupMode
      ? reviewAdGroups.filter(group => group.headlines.filter(h => h.trim()).length >= 3 && group.descriptions.filter(d => d.trim()).length >= 2).length
      : (validHeadlines.length >= 3 && validDescriptions.length >= 2 ? 1 : 0);

    return (
      <div style={styles.stepContent}>
        <h3 style={styles.stepTitle}>
          <Rocket size={24} style={{ color: '#ec4899', marginRight: 12 }} />
          Review & Create Campaign
        </h3>
        <p style={styles.stepDescription}>
          Review your campaign settings before creating
        </p>

        {/* Summary Cards */}
        <div style={styles.reviewGrid}>
          {/* Campaign Info */}
          <div style={styles.reviewCard}>
            <div style={styles.reviewCardHeader}>
              <Target size={18} style={{ color: '#8b5cf6' }} />
              <span>Campaign Info</span>
            </div>
            <div style={styles.reviewItem}>
              <span style={styles.reviewLabel}>Name:</span>
              <span style={styles.reviewValue}>{campaignName}</span>
            </div>
            <div style={styles.reviewItem}>
              <span style={styles.reviewLabel}>Ad Groups:</span>
              <span style={styles.reviewValue}>
                {isMultipleAdGroupMode
                  ? `${reviewAdGroups.length} ad groups`
                  : (adGroupName || `${campaignName} - Ad Group 1`)}
              </span>
            </div>
            {isMultipleAdGroupMode && reviewAdGroups.map((group, index) => (
              <div key={group.id} style={styles.reviewItem}>
                <span style={styles.reviewLabel}>#{index + 1}:</span>
                <span style={styles.reviewValue}>{group.name || `${campaignName} - Ad Group ${index + 1}`}</span>
              </div>
            ))}
            <div style={styles.reviewItem}>
              <span style={styles.reviewLabel}>Status:</span>
              <span style={{
                ...styles.reviewValue,
                color: campaignStatus === 'ENABLED' ? '#10b981' : '#f59e0b'
              }}>
                {campaignStatus === 'ENABLED' ? '✅ Enabled' : '⏸️ Paused'}
              </span>
            </div>
          </div>

          {/* Budget & Bidding */}
          <div style={styles.reviewCard}>
            <div style={styles.reviewCardHeader}>
              <DollarSign size={18} style={{ color: '#10b981' }} />
              <span>Budget & Bidding</span>
            </div>
            <div style={styles.reviewItem}>
              <span style={styles.reviewLabel}>Budget:</span>
              <span style={styles.reviewValue}>
                €{formatNumber(budgetAmount, 2)} {budgetType === 'daily' ? '/day' : 'total'}
              </span>
            </div>
            <div style={styles.reviewItem}>
              <span style={styles.reviewLabel}>Strategy:</span>
              <span style={styles.reviewValue}>
                {biddingStrategies.find(s => s.value === biddingStrategy)?.label}
              </span>
            </div>
            {biddingStrategy === 'MANUAL_CPC' && (
              <div style={styles.reviewItem}>
                <span style={styles.reviewLabel}>Max CPC:</span>
                <span style={styles.reviewValue}>€{formatNumber(maxCpcBid, 2)}</span>
              </div>
            )}
          </div>

          {/* Targeting */}
          <div style={styles.reviewCard}>
            <div style={styles.reviewCardHeader}>
              <Globe size={18} style={{ color: '#3b82f6' }} />
              <span>Targeting</span>
            </div>
            <div style={styles.reviewItem}>
              <span style={styles.reviewLabel}>Language:</span>
              <span style={styles.reviewValue}>
                {selectedLang?.flag} {selectedLang?.name}
              </span>
            </div>
            <div style={styles.reviewItem}>
              <span style={styles.reviewLabel}>Locations:</span>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                {selectedCountries.map(country => (
                  <span key={country.code} style={styles.reviewBadge}>
                    {country.flag} {country.name}
                    {country.type === 'city' && <span style={{ fontSize: 11, opacity: 0.7, marginLeft: 4 }}>(city)</span>}
                  </span>
                ))}
              </div>
            </div>
            <div style={styles.reviewItem}>
              <span style={styles.reviewLabel}>Networks:</span>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {networkSettings.targetGoogleSearch && (
                  <span style={styles.reviewBadge}>🔍 Google Search</span>
                )}
                {networkSettings.targetSearchNetwork && (
                  <span style={styles.reviewBadge}>🌐 Search Network</span>
                )}
                {networkSettings.targetContentNetwork && (
                  <span style={styles.reviewBadge}>📱 Display Expansion</span>
                )}
              </div>
            </div>
          </div>

          {/* Content */}
          <div style={styles.reviewCard}>
            <div style={styles.reviewCardHeader}>
              <Zap size={18} style={{ color: '#f59e0b' }} />
              <span>Content</span>
            </div>
            <div style={styles.reviewItem}>
              <span style={styles.reviewLabel}>Keywords:</span>
              <span style={styles.reviewValue}>{reviewKeywordCount}</span>
            </div>
            {isMultipleAdGroupMode && (
              <div style={styles.reviewItem}>
                <span style={styles.reviewLabel}>Ads:</span>
                <span style={styles.reviewValue}>{reviewAdCount}</span>
              </div>
            )}
            {!isMultipleAdGroupMode && (
              <>
            <div style={styles.reviewItem}>
              <span style={styles.reviewLabel}>Headlines:</span>
              <span style={styles.reviewValue}>{validHeadlines.length}</span>
            </div>
            <div style={styles.reviewItem}>
              <span style={styles.reviewLabel}>Descriptions:</span>
              <span style={styles.reviewValue}>{validDescriptions.length}</span>
            </div>
              </>
            )}
            {(path1 || path2) && (
              <div style={styles.reviewItem}>
                <span style={styles.reviewLabel}>URL Paths:</span>
                <span style={styles.reviewValue}>
                  {path1 && `/${path1}`}{path2 && `/${path2}`}
                </span>
              </div>
            )}
            {(sitelinks.length > 0 || callouts.length > 0 || structuredSnippets.length > 0) && (
              <div style={styles.reviewItem}>
                <span style={styles.reviewLabel}>Extensions:</span>
                <span style={styles.reviewValue}>
                  {sitelinks.length} sitelinks, {callouts.length} callouts, {structuredSnippets.length} snippets
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Preview */}
        <div style={{ ...styles.card, marginTop: 20 }}>
          <h4 style={styles.cardTitle}>Ad Preview</h4>
          {isMultipleAdGroupMode ? (
            <div style={{ display: 'grid', gap: 12 }}>
              {reviewAdGroups.map((group, index) => {
                const groupHeadlines = group.headlines.filter(h => h.trim());
                const groupDescriptions = group.descriptions.filter(d => d.trim());
                return (
                  <div key={group.id} style={styles.adPreview}>
                    <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: 12, marginBottom: 6 }}>
                      {group.name || `${campaignName || 'Campaign'} - Ad Group ${index + 1}`}
                    </div>
                    <div style={styles.adPreviewUrl}>{(group.finalUrl || '').replace(/^https?:\/\//, '')}</div>
                    <div style={styles.adPreviewHeadline}>
                      {groupHeadlines[0] || 'Headline 1'} | {groupHeadlines[1] || 'Headline 2'} | {groupHeadlines[2] || 'Headline 3'}
                    </div>
                    <div style={styles.adPreviewDescription}>
                      {groupDescriptions[0] || 'Description 1'}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div style={styles.adPreview}>
              <div style={styles.adPreviewUrl}>{finalUrl.replace(/^https?:\/\//, '')}</div>
              <div style={styles.adPreviewHeadline}>
                {validHeadlines[0] || 'Headline 1'} | {validHeadlines[1] || 'Headline 2'} | {validHeadlines[2] || 'Headline 3'}
              </div>
              <div style={styles.adPreviewDescription}>
                {validDescriptions[0] || 'Description 1'}
              </div>
            </div>
          )}
        </div>

        {/* Create Button */}
        {!createResult && (
          <button
            onClick={createCampaign}
            disabled={creating}
            style={{
              ...styles.createButton,
              opacity: creating ? 0.6 : 1,
              cursor: creating ? 'not-allowed' : 'pointer'
            }}
          >
            {creating ? (
              <>
                <div style={styles.spinner} />
                Creating Campaign...
              </>
            ) : (
              <>
                <Rocket size={20} />
                Create Campaign
              </>
            )}
          </button>
        )}

        {/* Result */}
        {createResult && (
          <div style={{
            ...styles.resultCard,
            background: createResult.success 
              ? 'rgba(16, 185, 129, 0.1)' 
              : 'rgba(239, 68, 68, 0.1)',
            borderColor: createResult.success 
              ? 'rgba(16, 185, 129, 0.3)' 
              : 'rgba(239, 68, 68, 0.3)'
          }}>
            {createResult.success ? (
              <>
                <CheckCircle size={48} style={{ color: '#10b981', marginBottom: 16 }} />
                <h3 style={{ color: 'white', marginBottom: 12 }}>Campaign Created Successfully!</h3>
                <p style={{ color: 'rgba(255,255,255,0.7)', marginBottom: 20 }}>
                  Your campaign has been created in Google Ads and is ready to go.
                </p>
                <div style={styles.resultDetails}>
                  <div>Campaign ID: {createResult.data?.campaignId || 'N/A'}</div>
                  <div>Status: {createResult.data?.status || 'paused'}</div>
                  {createResult.data?.adGroupCount > 1 && (
                    <div>Ad Groups Created: {createResult.data.adGroupCount}</div>
                  )}
                  <div>Keywords Added: {createResult.data?.keywords || 0}</div>
                  {createResult.data?.skippedKeywords > 0 && (
                    <div style={{ color: '#fbbf24' }}>
                      ⚠️ {createResult.data.skippedKeywords} keyword(s) skipped (over the 100-keyword limit)
                    </div>
                  )}
                  {createResult.data?.ads > 0 && <div>Ads Created: {createResult.data?.ads}</div>}
                  {createResult.data?.extensions && (
                    <div>
                      Extensions: {createResult.data.extensions.sitelinks || 0} sitelinks, {createResult.data.extensions.callouts || 0} callouts, {createResult.data.extensions.snippets || 0} snippets
                    </div>
                  )}
                  {(createResult.data?.extensionWarnings || []).map((w, i) => (
                    <div key={i} style={{ color: '#fbbf24' }}>⚠️ {w}</div>
                  ))}
                </div>
                
                {/* Success info box */}
                <div style={{
                  marginTop: 20,
                  padding: 16,
                  background: 'rgba(16, 185, 129, 0.1)',
                  border: '1px solid rgba(16, 185, 129, 0.3)',
                  borderRadius: 8,
                  color: '#10b981',
                  fontSize: 14
                }}>
                  <strong>Your campaign is now live in Google Ads!</strong>
                  <div style={{ color: 'rgba(255,255,255,0.7)', marginTop: 8, fontSize: 13 }}>
                    The campaign is created in PAUSED status. Go to Google Ads to review and enable it when ready.
                  </div>
                </div>
              </>
            ) : (
              <>
                <AlertCircle size={48} style={{ color: '#ef4444', marginBottom: 16 }} />
                <h3 style={{ color: 'white', marginBottom: 12 }}>Campaign Creation Failed</h3>
                <div style={{ 
                  color: 'rgba(255,255,255,0.8)', 
                  textAlign: 'left',
                  background: 'rgba(0,0,0,0.2)',
                  padding: 20,
                  borderRadius: 8,
                  marginTop: 20,
                  whiteSpace: 'pre-wrap',
                  fontSize: 14,
                  lineHeight: 1.6
                }}>
                  {createResult.error || 'An error occurred while creating the campaign'}
                </div>
                {createResult.apiLimited && (
                  <div style={{
                    marginTop: 20,
                    padding: 16,
                    background: 'rgba(59, 130, 246, 0.1)',
                    border: '1px solid rgba(59, 130, 246, 0.3)',
                    borderRadius: 8,
                    color: '#60a5fa',
                    fontSize: 13
                  }}>
                    💡 <strong>Tip:</strong> While API access is limited, you can still use the Ad Creator to generate high-quality ad content and copy it manually to Google Ads.
                  </div>
                )}
              </>
            )}
          </div>
        )}
        
        {/* Publish Confirmation Modal */}
        {showPublishModal && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999
          }}>
            <div style={{
              background: 'var(--bg-secondary)',
              borderRadius: 20,
              padding: 40,
              maxWidth: 500,
              width: '90%'
            }}>
              <h3 style={{ color: 'white', fontSize: 24, fontWeight: 700, marginBottom: 16 }}>
                📤 Publish to Google Ads
              </h3>
              
              <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: 15, marginBottom: 24, lineHeight: 1.6 }}>
                You're about to publish this campaign to Google Ads. The campaign will be created in a <strong style={{ color: 'white' }}>PAUSED</strong> state, so you can review it before enabling it.
              </p>
              
              <div style={{
                background: 'rgba(59,130,246,0.1)',
                border: '1px solid rgba(59,130,246,0.3)',
                borderRadius: 12,
                padding: 16,
                marginBottom: 24
              }}>
                <div style={{ color: 'white', fontSize: 14, marginBottom: 8 }}>
                  <strong>Campaign Name:</strong> {campaignName}
                </div>
                <div style={{ color: 'white', fontSize: 14, marginBottom: 8 }}>
                  <strong>Budget:</strong> €{budgetAmount}/day
                </div>
                <div style={{ color: 'white', fontSize: 14, marginBottom: 8 }}>
                  <strong>Keywords:</strong> {keywords.length}
                </div>
                <div style={{ color: 'white', fontSize: 14 }}>
                  <strong>Ads:</strong> {headlines.filter(h => h.trim()).length} headlines
                </div>
              </div>
              
              <div style={{ display: 'flex', gap: 12 }}>
                <button
                  onClick={publishCampaign}
                  disabled={publishing}
                  style={{
                    flex: 1,
                    padding: 14,
                    background: publishing ? 'rgba(59,130,246,0.3)' : 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                    border: 'none',
                    borderRadius: 10,
                    color: 'white',
                    fontSize: 15,
                    fontWeight: 600,
                    cursor: publishing ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 8
                  }}
                >
                  {publishing ? (
                    <>
                      <div style={styles.spinner} />
                      Publishing...
                    </>
                  ) : (
                    <>
                      <Rocket size={18} />
                      Publish Now
                    </>
                  )}
                </button>
                <button
                  onClick={() => setShowPublishModal(false)}
                  disabled={publishing}
                  style={{
                    padding: 14,
                    background: 'rgba(255,255,255,0.1)',
                    border: '1px solid rgba(255,255,255,0.2)',
                    borderRadius: 10,
                    color: 'white',
                    fontSize: 15,
                    fontWeight: 600,
                    cursor: publishing ? 'not-allowed' : 'pointer'
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const styles = {
    container: {
      padding: 40,
      maxWidth: 1400,
      margin: '0 auto'
    },
    header: {
      marginBottom: 40
    },
    title: {
      color: 'white',
      fontSize: 32,
      fontWeight: 700,
      marginBottom: 8,
      background: 'linear-gradient(135deg, #8b5cf6 0%, #3b82f6 100%)',
      backgroundClip: 'text',
      WebkitBackgroundClip: 'text',
      color: 'transparent'
    },
    subtitle: {
      color: 'rgba(255, 255, 255, 0.6)',
      fontSize: 16
    },
    stepperContainer: {
      background: 'rgba(255, 255, 255, 0.03)',
      borderRadius: 20,
      padding: 30,
      marginBottom: 30,
      border: '1px solid rgba(255, 255, 255, 0.1)'
    },
    stepper: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      position: 'relative'
    },
    stepperLine: {
      position: 'absolute',
      top: 24,
      left: 0,
      right: 0,
      height: 2,
      background: 'rgba(255, 255, 255, 0.1)',
      zIndex: 0
    },
    stepperLineActive: {
      position: 'absolute',
      top: 24,
      left: 0,
      height: 2,
      background: 'linear-gradient(90deg, #8b5cf6, #3b82f6)',
      zIndex: 0,
      transition: 'width 0.3s ease'
    },
    step: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      flex: 1,
      position: 'relative',
      zIndex: 1
    },
    stepCircle: {
      width: 48,
      height: 48,
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      marginBottom: 12,
      transition: 'all 0.3s ease',
      cursor: 'pointer'
    },
    stepCircleInactive: {
      background: 'rgba(255, 255, 255, 0.05)',
      border: '2px solid rgba(255, 255, 255, 0.2)',
      color: 'rgba(255, 255, 255, 0.4)'
    },
    stepCircleActive: {
      background: 'linear-gradient(135deg, #8b5cf6, #3b82f6)',
      border: '2px solid transparent',
      color: 'white',
      boxShadow: '0 0 20px rgba(139, 92, 246, 0.4)'
    },
    stepCircleCompleted: {
      background: 'rgba(16, 185, 129, 0.2)',
      border: '2px solid #10b981',
      color: '#10b981'
    },
    stepLabel: {
      fontSize: 13,
      fontWeight: 600,
      textAlign: 'center',
      color: 'rgba(255, 255, 255, 0.5)',
      transition: 'color 0.3s ease'
    },
    stepLabelActive: {
      color: 'white'
    },
    contentContainer: {
      background: 'rgba(255, 255, 255, 0.03)',
      borderRadius: 20,
      padding: 40,
      border: '1px solid rgba(255, 255, 255, 0.1)',
      minHeight: 600
    },
    stepContent: {
      color: 'white'
    },
    stepTitle: {
      fontSize: 24,
      fontWeight: 700,
      marginBottom: 8,
      display: 'flex',
      alignItems: 'center'
    },
    stepDescription: {
      color: 'rgba(255, 255, 255, 0.6)',
      marginBottom: 30,
      fontSize: 15
    },
    card: {
      background: 'rgba(255, 255, 255, 0.03)',
      borderRadius: 12,
      padding: 24,
      border: '1px solid rgba(255, 255, 255, 0.1)'
    },
    cardTitle: {
      fontSize: 18,
      fontWeight: 600,
      marginBottom: 20,
      color: 'white'
    },
    formGroup: {
      marginBottom: 24
    },
    label: {
      display: 'block',
      fontSize: 14,
      fontWeight: 600,
      marginBottom: 8,
      color: 'rgba(255, 255, 255, 0.9)'
    },
    input: {
      width: '100%',
      padding: '12px 16px',
      background: 'rgba(255, 255, 255, 0.05)',
      border: '1px solid rgba(255, 255, 255, 0.1)',
      borderRadius: 8,
      color: 'white',
      fontSize: 14,
      outline: 'none',
      transition: 'all 0.3s ease',
      marginBottom: 8,
      boxSizing: 'border-box'
    },
    select: {
      width: '100%',
      padding: '12px 16px',
      background: 'rgba(255, 255, 255, 0.05)',
      border: '1px solid rgba(255, 255, 255, 0.1)',
      borderRadius: 8,
      color: 'white',
      fontSize: 14,
      outline: 'none',
      cursor: 'pointer',
      marginBottom: 8
    },
    hint: {
      fontSize: 12,
      color: 'rgba(255, 255, 255, 0.5)',
      marginTop: 4
    },
    radioGroup: {
      display: 'grid',
      gap: 12
    },
    radioLabel: {
      display: 'flex',
      alignItems: 'flex-start',
      gap: 12,
      padding: 16,
      background: 'rgba(255, 255, 255, 0.03)',
      borderRadius: 8,
      border: '1px solid rgba(255, 255, 255, 0.1)',
      cursor: 'pointer',
      transition: 'all 0.3s ease'
    },
    radio: {
      marginTop: 2,
      cursor: 'pointer'
    },
    radioHint: {
      fontSize: 12,
      color: 'rgba(255, 255, 255, 0.5)',
      marginTop: 4
    },
    buttonGroup: {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: 12
    },
    optionButton: {
      padding: 16,
      background: 'rgba(255, 255, 255, 0.03)',
      border: '1px solid rgba(255, 255, 255, 0.1)',
      borderRadius: 8,
      color: 'rgba(255, 255, 255, 0.7)',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      textAlign: 'left',
      fontSize: 14
    },
    optionButtonActive: {
      background: 'rgba(139, 92, 246, 0.2)',
      border: '1px solid #8b5cf6',
      color: 'white'
    },
    inputGroup: {
      position: 'relative'
    },
    inputPrefix: {
      position: 'absolute',
      left: 16,
      top: 12,
      color: 'rgba(255, 255, 255, 0.5)',
      fontSize: 14,
      fontWeight: 600
    },
    languageGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
      gap: 12
    },
    languageButton: {
      padding: 16,
      background: 'rgba(255, 255, 255, 0.03)',
      border: '1px solid rgba(255, 255, 255, 0.1)',
      borderRadius: 8,
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      textAlign: 'center',
      color: 'rgba(255, 255, 255, 0.7)',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center'
    },
    languageButtonActive: {
      background: 'rgba(59, 130, 246, 0.2)',
      border: '1px solid #3b82f6',
      color: 'white'
    },
    checkboxGroup: {
      display: 'flex',
      flexDirection: 'column',
      gap: 12
    },
    checkboxLabel: {
      display: 'flex',
      gap: 12,
      padding: 16,
      background: 'rgba(255, 255, 255, 0.03)',
      borderRadius: 8,
      border: '1px solid rgba(255, 255, 255, 0.1)',
      cursor: 'pointer'
    },
    checkbox: {
      marginTop: 2,
      cursor: 'pointer'
    },
    expandButton: {
      width: '100%',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: 16,
      background: 'none',
      border: 'none',
      color: 'white',
      fontSize: 16,
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'opacity 0.3s ease'
    },
    keywordInputRow: {
      display: 'flex',
      gap: 12,
      marginBottom: 16
    },
    addButton: {
      padding: '12px 20px',
      background: 'linear-gradient(135deg, #8b5cf6, #3b82f6)',
      border: 'none',
      borderRadius: 8,
      color: 'white',
      fontWeight: 600,
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: 8,
      transition: 'transform 0.2s ease'
    },
    keywordList: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: 8
    },
    keywordChip: {
      display: 'flex',
      alignItems: 'center',
      gap: 8,
      padding: '8px 12px',
      background: 'rgba(139, 92, 246, 0.2)',
      border: '1px solid rgba(139, 92, 246, 0.4)',
      borderRadius: 8,
      color: 'white',
      fontSize: 13
    },
    keywordMatchType: {
      fontSize: 11,
      padding: '2px 6px',
      background: 'rgba(255, 255, 255, 0.1)',
      borderRadius: 4,
      color: 'rgba(255, 255, 255, 0.6)'
    },
    removeButton: {
      background: 'none',
      border: 'none',
      color: 'rgba(255, 255, 255, 0.5)',
      cursor: 'pointer',
      padding: 4,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      transition: 'color 0.2s ease'
    },
    smallAddButton: {
      padding: '6px 12px',
      background: 'rgba(139, 92, 246, 0.2)',
      border: '1px solid rgba(139, 92, 246, 0.4)',
      borderRadius: 6,
      color: 'white',
      fontSize: 13,
      fontWeight: 600,
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: 4
    },
    adInputRow: {
      display: 'flex',
      gap: 12,
      alignItems: 'flex-start',
      marginBottom: 12
    },
    charCount: {
      fontSize: 12,
      color: 'rgba(255, 255, 255, 0.5)',
      whiteSpace: 'nowrap',
      paddingTop: 14
    },
    reviewGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
      gap: 20,
      marginBottom: 20
    },
    reviewCard: {
      background: 'rgba(255, 255, 255, 0.03)',
      borderRadius: 12,
      padding: 20,
      border: '1px solid rgba(255, 255, 255, 0.1)'
    },
    reviewCardHeader: {
      display: 'flex',
      alignItems: 'center',
      gap: 8,
      fontSize: 16,
      fontWeight: 600,
      marginBottom: 16,
      color: 'white'
    },
    reviewItem: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      marginBottom: 12,
      fontSize: 14
    },
    reviewLabel: {
      color: 'rgba(255, 255, 255, 0.6)',
      marginRight: 12
    },
    reviewValue: {
      color: 'white',
      fontWeight: 600,
      textAlign: 'right'
    },
    reviewBadge: {
      fontSize: 12,
      padding: '4px 8px',
      background: 'rgba(59, 130, 246, 0.2)',
      border: '1px solid rgba(59, 130, 246, 0.4)',
      borderRadius: 4,
      color: '#60a5fa'
    },
    adPreview: {
      padding: 20,
      background: 'white',
      borderRadius: 8,
      color: '#1a1a1a'
    },
    adPreviewUrl: {
      color: '#5f6368',
      fontSize: 14,
      marginBottom: 4
    },
    adPreviewHeadline: {
      color: '#1a0dab',
      fontSize: 20,
      fontWeight: 400,
      marginBottom: 4
    },
    adPreviewDescription: {
      color: '#4d5156',
      fontSize: 14,
      lineHeight: 1.5
    },
    createButton: {
      width: '100%',
      padding: '16px 32px',
      marginTop: 20,
      background: 'linear-gradient(135deg, #10b981, #059669)',
      border: 'none',
      borderRadius: 12,
      color: 'white',
      fontSize: 18,
      fontWeight: 700,
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 12,
      transition: 'transform 0.2s ease',
      boxShadow: '0 10px 30px rgba(16, 185, 129, 0.3)'
    },
    navigationButtons: {
      display: 'flex',
      justifyContent: 'space-between',
      marginTop: 30
    },
    navButton: {
      padding: '12px 24px',
      background: 'rgba(255, 255, 255, 0.05)',
      border: '1px solid rgba(255, 255, 255, 0.2)',
      borderRadius: 8,
      color: 'white',
      fontSize: 14,
      fontWeight: 600,
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: 8,
      transition: 'all 0.3s ease'
    },
    navButtonPrimary: {
      background: 'linear-gradient(135deg, #8b5cf6, #3b82f6)',
      border: 'none'
    },
    navButtonDisabled: {
      opacity: 0.4,
      cursor: 'not-allowed'
    },
    resultCard: {
      marginTop: 20,
      padding: 40,
      borderRadius: 12,
      border: '1px solid',
      textAlign: 'center'
    },
    resultDetails: {
      display: 'flex',
      flexDirection: 'column',
      gap: 8,
      color: 'rgba(255, 255, 255, 0.7)',
      fontSize: 14
    },
    spinner: {
      width: 20,
      height: 20,
      border: '3px solid rgba(255, 255, 255, 0.3)',
      borderTop: '3px solid white',
      borderRadius: '50%',
      animation: 'spin 1s linear infinite'
    }
  };

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <h1 style={styles.title}>Campaign Builder</h1>
          {initialData?.isCloned && (
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              background: 'rgba(139,92,246,0.2)',
              border: '1px solid rgba(139,92,246,0.4)',
              borderRadius: 6,
              padding: '4px 10px',
              fontSize: 12,
              color: '#8b5cf6',
              fontWeight: 600
            }}>
              <Copy size={14} />
              Cloned Campaign
            </div>
          )}
        </div>
        <p style={styles.subtitle}>Create a new Google Ads Search campaign with complete control</p>
      </div>

      {/* Stepper */}
      <div style={styles.stepperContainer}>
        <div style={styles.stepper}>
          <div style={styles.stepperLine} />
          <div 
            style={{
              ...styles.stepperLineActive,
              width: `${((currentStep - 1) / (steps.length - 1)) * 100}%`
            }} 
          />
          
          {steps.map((step) => {
            const Icon = step.icon;
            const isActive = currentStep === step.number;
            const isCompleted = currentStep > step.number;
            
            return (
              <div key={step.number} style={styles.step}>
                <div
                  style={{
                    ...styles.stepCircle,
                    ...(isCompleted ? styles.stepCircleCompleted : 
                        isActive ? styles.stepCircleActive : 
                        styles.stepCircleInactive)
                  }}
                  onClick={() => setCurrentStep(step.number)}
                >
                  {isCompleted ? (
                    <CheckCircle size={24} />
                  ) : (
                    <Icon size={24} />
                  )}
                </div>
                <div style={{
                  ...styles.stepLabel,
                  ...(isActive ? styles.stepLabelActive : {})
                }}>
                  {step.title}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Content */}
      <div style={styles.contentContainer}>
        {renderStepContent()}

        {/* Navigation */}
        {currentStep < 5 && (
          <div style={styles.navigationButtons}>
            <button
              onClick={() => setCurrentStep(Math.max(1, currentStep - 1))}
              disabled={currentStep === 1}
              style={{
                ...styles.navButton,
                ...(currentStep === 1 ? styles.navButtonDisabled : {})
              }}
            >
              Previous
            </button>
            
            <button
              onClick={() => setCurrentStep(Math.min(5, currentStep + 1))}
              disabled={!canProceedToNextStep()}
              style={{
                ...styles.navButton,
                ...styles.navButtonPrimary,
                ...(!canProceedToNextStep() ? styles.navButtonDisabled : {})
              }}
            >
              Next
              <ArrowRight size={18} />
            </button>
          </div>
        )}
      </div>

      {/* AI Generation Modal */}
      {showAiModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'var(--bg-secondary)',
            borderRadius: 20,
            padding: 40,
            maxWidth: 600,
            width: '90%',
            maxHeight: '90vh',
            overflowY: 'auto',
            border: '1px solid rgba(139, 92, 246, 0.3)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
              <h3 style={{ color: 'white', fontSize: 24, fontWeight: 700, margin: 0, display: 'flex', alignItems: 'center', gap: 10 }}>
                <Sparkles size={28} style={{ color: '#a78bfa' }} />
                AI Ad Generation
              </h3>
              <button
                onClick={() => setShowAiModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'rgba(255, 255, 255, 0.5)',
                  cursor: 'pointer',
                  padding: 4
                }}
              >
                <X size={24} />
              </button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
              {/* Business Info */}
              <div>
                <label style={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: 14, display: 'block', marginBottom: 8 }}>
                  Website (Optional)
                </label>
                <input
                  type="url"
                  value={businessInfo.website}
                  onChange={(e) => setBusinessInfo(prev => ({ ...prev, website: e.target.value }))}
                  placeholder="https://example.com"
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    background: 'rgba(255, 255, 255, 0.05)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    borderRadius: 8,
                    color: 'white',
                    fontSize: 14,
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              <div>
                <label style={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: 14, display: 'block', marginBottom: 8 }}>
                  USPs / Key Benefits (Optional)
                </label>
                <textarea
                  value={businessInfo.usps}
                  onChange={(e) => setBusinessInfo(prev => ({ ...prev, usps: e.target.value }))}
                  placeholder="e.g., Free shipping, 30-day returns, expert support..."
                  rows={3}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    background: 'rgba(255, 255, 255, 0.05)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    borderRadius: 8,
                    color: 'white',
                    fontSize: 14,
                    resize: 'vertical',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              {/* Landing Page */}
              <div>
                <label style={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: 14, display: 'block', marginBottom: 8 }}>
                  Landing Page URL (Optional)
                </label>
                <input
                  type="url"
                  value={landingPageUrl}
                  onChange={(e) => setLandingPageUrl(e.target.value)}
                  placeholder="https://example.com/product"
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    background: 'rgba(255, 255, 255, 0.05)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    borderRadius: 8,
                    color: 'white',
                    fontSize: 14,
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              {/* Competitor Analysis */}
              <label style={{ 
                color: 'rgba(255, 255, 255, 0.8)', 
                fontSize: 14, 
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                cursor: 'pointer'
              }}>
                <input
                  type="checkbox"
                  checked={analyzeCompetitors}
                  onChange={(e) => setAnalyzeCompetitors(e.target.checked)}
                  style={{ width: 18, height: 18, cursor: 'pointer' }}
                />
                Analyze Competitor Ads
              </label>

              <div style={{
                background: 'rgba(59, 130, 246, 0.1)',
                border: '1px solid rgba(59, 130, 246, 0.3)',
                borderRadius: 8,
                padding: 12,
                fontSize: 13,
                color: 'rgba(255, 255, 255, 0.7)'
              }}>
                <Info size={16} style={{ display: 'inline', marginRight: 6, verticalAlign: 'middle' }} />
                AI will use your keywords and business context to generate compelling ad copy
              </div>

              {/* Action Buttons */}
              <div style={{ display: 'flex', gap: 12, marginTop: 8 }}>
                <button
                  onClick={() => setShowAiModal(false)}
                  disabled={aiGenerating}
                  style={{
                    flex: 1,
                    padding: '12px 24px',
                    background: 'rgba(255, 255, 255, 0.1)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    borderRadius: 8,
                    color: 'white',
                    cursor: aiGenerating ? 'not-allowed' : 'pointer',
                    fontSize: 14,
                    fontWeight: 600
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={generateAdsWithAI}
                  disabled={aiGenerating}
                  style={{
                    flex: 1,
                    padding: '12px 24px',
                    background: aiGenerating ? 'rgba(139, 92, 246, 0.5)' : 'linear-gradient(135deg, #8b5cf6, #a78bfa)',
                    border: 'none',
                    borderRadius: 8,
                    color: 'white',
                    cursor: aiGenerating ? 'not-allowed' : 'pointer',
                    fontSize: 14,
                    fontWeight: 600,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 8
                  }}
                >
                  {aiGenerating ? (
                    <>
                      <RefreshCw size={16} style={{ animation: 'spin 1s linear infinite' }} />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Sparkles size={16} />
                      Generate Ads
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Country Selection Modal */}
      {showCountryModal && language && languageCountries[language] && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            padding: 20
          }}
          onClick={closeCountryModal}
        >
          <div
            style={{
              background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
              borderRadius: 24,
              padding: 40,
              maxWidth: 700,
              width: '100%',
              maxHeight: '90vh',
              overflow: 'auto',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div style={{ marginBottom: 30 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                <h2 style={{ color: 'white', fontSize: 24, fontWeight: 700, margin: 0, display: 'flex', alignItems: 'center', gap: 12 }}>
                  <Globe size={28} style={{ color: '#10b981' }} />
                  Select Target Locations
                </h2>
                <button
                  onClick={closeCountryModal}
                  style={{
                    background: 'rgba(255, 255, 255, 0.1)',
                    border: 'none',
                    borderRadius: 10,
                    width: 36,
                    height: 36,
                    color: 'rgba(255, 255, 255, 0.6)',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 20,
                    fontWeight: 600
                  }}
                >
                  ×
                </button>
              </div>
              <p style={{ color: 'rgba(255, 255, 255, 0.6)', margin: 0, fontSize: 14 }}>
                Choose which countries you want to target with your campaign. 
                Selected: {tempSelectedCountries.length} / {languageCountries[language].length}
              </p>
            </div>

            {/* Select All / Deselect All */}
            <div style={{ marginBottom: 20 }}>
              <button
                onClick={toggleAllTempCountries}
                style={{
                  padding: '8px 16px',
                  background: 'rgba(16, 185, 129, 0.1)',
                  border: '1px solid rgba(16, 185, 129, 0.3)',
                  borderRadius: 10,
                  color: '#10b981',
                  fontSize: 13,
                  cursor: 'pointer',
                  fontWeight: 600
                }}
              >
                {tempSelectedCountries.length === languageCountries[language].length ? '☑ Deselect All' : '☐ Select All'}
              </button>
            </div>

            {/* Country Grid */}
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', 
              gap: 12,
              marginBottom: 30
            }}>
              {languageCountries[language].map(country => {
                const isSelected = tempSelectedCountries.some(c => c.code === country.code);
                return (
                  <button
                    key={country.code}
                    onClick={() => toggleTempCountrySelection(country)}
                    disabled={isSelected && tempSelectedCountries.length === 1}
                    style={{
                      padding: '14px 16px',
                      background: isSelected 
                        ? 'rgba(16, 185, 129, 0.2)' 
                        : 'rgba(255, 255, 255, 0.05)',
                      border: `2px solid ${isSelected ? '#10b981' : 'rgba(255, 255, 255, 0.1)'}`,
                      borderRadius: 12,
                      color: isSelected ? '#10b981' : 'rgba(255, 255, 255, 0.8)',
                      cursor: (isSelected && tempSelectedCountries.length === 1) ? 'not-allowed' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 12,
                      fontSize: 14,
                      fontWeight: 500,
                      transition: 'all 0.2s',
                      position: 'relative',
                      opacity: (isSelected && tempSelectedCountries.length === 1) ? 0.6 : 1,
                      textAlign: 'left'
                    }}
                  >
                    <span style={{ fontSize: 24 }}>{country.flag}</span>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600, marginBottom: 2 }}>{country.name}</div>
                      <div style={{ fontSize: 11, opacity: 0.7 }}>Geo: {country.geoCode}</div>
                    </div>
                    {isSelected ? (
                      <CheckSquare size={20} />
                    ) : (
                      <Square size={20} style={{ opacity: 0.4 }} />
                    )}
                  </button>
                );
              })}
            </div>

            {/* Modal Footer */}
            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', paddingTop: 20, borderTop: '1px solid rgba(255, 255, 255, 0.1)' }}>
              <button
                onClick={closeCountryModal}
                style={{
                  padding: '12px 24px',
                  background: 'rgba(255, 255, 255, 0.05)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  borderRadius: 12,
                  color: 'rgba(255, 255, 255, 0.7)',
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={applyCountrySelection}
                style={{
                  padding: '12px 32px',
                  background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  border: 'none',
                  borderRadius: 12,
                  color: 'white',
                  fontSize: 14,
                  fontWeight: 700,
                  cursor: 'pointer',
                  boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)'
                }}
              >
                Apply Selection ({tempSelectedCountries.length})
              </button>
            </div>
          </div>
        </div>
      )}

      {/* CSS for spinner animation */}
      <style>
        {`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
          
          input:focus, select:focus, textarea:focus {
            border-color: #8b5cf6 !important;
            outline: none;
          }
          
          button:hover:not(:disabled) {
            transform: translateY(-2px);
          }
          
          button:active:not(:disabled) {
            transform: translateY(0);
          }
        `}
      </style>
    </div>
  );
}

